import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';

const MapViz = lazy(() => import('../components/MapViz'));
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import MarketingTags from '@/components/MarketingTags';
import { ADMIN_LANDED_KEY, landingUrlFor } from '@/constants/navigation';
import { HOME_FAQS } from '@/content/faqs';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '@/constants/support';
import { SHOW_REFUNDABLE_PERKS_CARDS } from '@/constants/promoCards';
import { Button } from '@/components/ui/button';
import AirportInput from '../components/search/AirportInput';
import PassengerSelector from '../components/search/PassengerSelector';
import CabinClassSelector from '../components/search/CabinClassSelector';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { CalendarWithPrices as CalendarComponent, CalendarRangeStyles } from '@/components/ui/CalendarWithPrices';
import CalendarDock from '@/components/ui/CalendarDock';

/** First selectable departure day (22 days out) — calendars open here instead of a fully-disabled current month. */
const getMinBookableDate = () => new Date(new Date().setDate(new Date().getDate() + 22));

import { useFlightCalendarPrices } from '@/hooks/useFlightCalendarPrices';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, CreditCard, Clock, CheckCircle2, CheckCircle, Star, ChevronLeft, ChevronRight, Plane, Users, ArrowRight, Award, Check, X, Armchair, Wine, Gift, Sparkles, Zap, Search, Wallet, Lock, Percent, Phone } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { generateSearchActivity } from '@/data/searchActivityData';
import { isNativeApp } from '@/native/bootstrap';
import BeautyOfFreedomVideo from '@/components/marketing/BeautyOfFreedomVideo';
import PageSeo from '@/components/PageSeo';
import { buildFlightSearchParams, prefetchFlightSearch } from '@/lib/searchPrefetch';
import { initialSearchTravelers } from '@/lib/usualTravelers';
import { useSearchPickers } from '@/hooks/useSearchPickers';
import { checkBuildWhileSearching, openSearchResults } from '@/lib/searchNavigation';
import { writePassengerParams } from '@/utils/passengerTypes';
import { normalizeCabinClass } from '@/utils/cabinClass';

// Customer reviews data
const customerReviews = [
  { id: 1, name: 'Sarah M.', location: 'Miami, FL', rating: 5, text: 'Defera made my dream trip to Hawaii possible! The payment plan was so flexible, and I could pay over time instead of all at once.', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face' },
  { id: 2, name: 'Marcus J.', location: 'Chicago, IL', rating: 5, text: 'I was skeptical at first, but this service is incredible. Booked a family trip to Disney without the financial stress.', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face' },
  { id: 3, name: 'Emily C.', location: 'San Francisco, CA', rating: 5, text: 'Finally a travel service that understands real people! The installment plans are transparent, no hidden fees.', avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face' },
  { id: 4, name: 'David R.', location: 'Austin, TX', rating: 5, text: 'Great experience booking my honeymoon. The payment flexibility gave us time to save while securing the flights we wanted.', avatar: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=150&h=150&fit=crop&crop=face' },
  { id: 5, name: 'Jessica P.', location: 'Seattle, WA', rating: 5, text: 'The payment flexibility is incredible! I can actually afford to travel more often now without worrying about huge upfront costs.', avatar: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=150&h=150&fit=crop&crop=face' },
  { id: 6, name: 'Chris A.', location: 'New York, NY', rating: 5, text: 'Booking last-minute flights used to stress me out financially. With Defera, I can travel spontaneously and pay over time.', avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=150&h=150&fit=crop&crop=face' },
  { id: 7, name: 'Amanda L.', location: 'Los Angeles, CA', rating: 5, text: 'Never thought I could afford premium flights. Defera made it accessible with their payment plans.', avatar: 'https://images.unsplash.com/photo-1619895862022-09114b41f16f?w=150&h=150&fit=crop&crop=face' },
  { id: 8, name: 'Michael T.', location: 'Boston, MA', rating: 5, text: 'Smooth booking process from start to finish. The mobile app is intuitive, and I love tracking my payments in one place.', avatar: 'https://images.unsplash.com/photo-1552374196-c4e7ffc6e126?w=150&h=150&fit=crop&crop=face' },
  { id: 9, name: 'Rachel M.', location: 'Denver, CO', rating: 5, text: 'I travel for work frequently, and Defera has become my go-to platform. The installment plans make expense management so much easier.', avatar: 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=150&h=150&fit=crop&crop=face' },
  { id: 10, name: 'Brandon W.', location: 'Atlanta, GA', rating: 5, text: 'Just booked my third trip through Defera. The payment plans fit perfectly with my budget, and the service just keeps getting better.', avatar: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=150&h=150&fit=crop&crop=face' },
  { id: 11, name: 'Nicole T.', location: 'Phoenix, AZ', rating: 5, text: 'As a single parent, affording family vacations was tough. Defera\'s installment option lets us make memories without breaking the bank.', avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=150&h=150&fit=crop&crop=face' },
  { id: 12, name: 'Kevin P.', location: 'Dallas, TX', rating: 5, text: 'The customer service team went above and beyond when I had questions about my booking. Fast, friendly, and knowledgeable.', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=150&h=150&fit=crop&crop=face' },
];

// Defera vs. traditional BNPL comparison rows (shared by both cards)
const bnplComparison = [
  { label: 'Interest', bnpl: 'May apply', defera: '0% — always' },
  { label: 'Credit check', bnpl: 'Often required', defera: 'Never required' },
  { label: 'Spending limit', bnpl: 'May not cover full trip', defera: 'Unlimited bookings' },
  { label: 'Price protection', bnpl: 'Not guaranteed', defera: 'Locked from day one' },
  { label: 'Built for', bnpl: 'Retail & e-commerce', defera: 'Flights & travel' },
];

// Animated phone mockup that cycles through the booking flow.
// Used by the "How it works" section above Triple Dip.
function HowItWorksPhone() {
  const [step, setStep] = React.useState(0);
  const totalSteps = 5;

  React.useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % totalSteps), 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative mx-auto" style={{ width: 'min(320px, 80vw)' }}>
      {/* Ambient glow behind phone */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-16 opacity-80"
        style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(16, 37, 178,0.22), transparent 70%)' }}
      />
      {/* Phone frame */}
      <div
        className="relative rounded-[44px] p-[3px]"
        style={{
          background: 'linear-gradient(160deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04) 40%, rgba(255,255,255,0.02))',
          boxShadow:
            '0 40px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.10)',
        }}
      >
        <div className="relative rounded-[42px] overflow-hidden bg-[#0a0a0c]" style={{ aspectRatio: '9 / 19.5' }}>
          {/* Notch / Dynamic Island */}
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 w-[88px] h-[26px] rounded-full bg-black" />
          {/* Status bar */}
          <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-6 pt-2 pb-1 text-[10px] text-white/70 font-normal">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M0 8h2V5H0v3zm3 0h2V3H3v5zm3 0h2V1H6v7zm3 0h2V0H9v8z" fill="currentColor"/></svg>
              <svg width="12" height="8" viewBox="0 0 16 12" fill="none"><path d="M8 3c2.21 0 4.21.84 5.66 2.34l1.41-1.42A9.99 9.99 0 0 0 8 1C5.45 1 3.13 1.95 1.34 3.51L2.76 4.93A7.97 7.97 0 0 1 8 3zm0 3c-1.66 0-3.16.67-4.24 1.76L8 12l4.24-4.24A6 6 0 0 0 8 6z" fill="currentColor"/></svg>
              <div className="w-5 h-2.5 rounded-[2px] border border-white/60 relative">
                <div className="absolute inset-0.5 right-1 bg-white/60 rounded-[1px]" />
              </div>
            </div>
          </div>
          {/* Animated screen content */}
          <div className="absolute inset-0 pt-12">
            <AnimatePresence mode="wait">
              {step === 0 && <PhoneSearch key="search" />}
              {step === 1 && <PhoneResults key="results" />}
              {step === 2 && <PhoneSeats key="seats" />}
              {step === 3 && <PhonePlan key="plan" />}
              {step === 4 && <PhonePay key="pay" />}
            </AnimatePresence>
          </div>
          {/* Step indicator dots at bottom */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Show step ${i + 1}`}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === step ? 18 : 5,
                  height: 5,
                  background: i === step ? 'rgba(122,138,191,0.95)' : 'rgba(255,255,255,0.25)',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const phoneScreenAnim = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

// Glass pill style shared by all phone inputs/buttons — mirrors the production
// `pillGlassClass` used in GoogleFlightsSearch.
const phoneGlassPill = {
  background:
    'linear-gradient(0deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.02) 100%)',
  boxShadow:
    'inset 0 0.5px 0 0 rgba(0,0,0,0.58), inset 0 0 0 0.5px rgba(255,255,255,0.12), 0 1px 0 0 rgba(255,255,255,0.04)',
};

function PhoneSearch() {
  return (
    <motion.div {...phoneScreenAnim} className="h-full flex flex-col px-4 pt-2 pb-10">
      {/* Trip type toggles — mirrors search page */}
      <div className="flex items-center gap-1.5 mb-4">
        <div className="px-3 py-1 rounded-full text-[10px] font-medium bg-white text-black">Flights</div>
        <div className="px-3 py-1 rounded-full text-[10px] font-normal text-white/55 bg-white/5">Stays</div>
        <div className="px-3 py-1 rounded-full text-[10px] font-normal text-white/55 bg-white/5">Explore</div>
      </div>
      <div className="space-y-2.5">
        {/* From */}
        <div className="relative rounded-2xl backdrop-blur-md border-[0.5px] border-white/20 px-3 h-[42px] flex items-center" style={phoneGlassPill}>
          <Plane className="w-3.5 h-3.5 text-white/60 mr-2.5 -rotate-45 flex-shrink-0" />
          <div className="text-[12px] text-white/95 font-normal">San Francisco (SFO)</div>
        </div>
        {/* To */}
        <div className="relative rounded-2xl backdrop-blur-md border-[0.5px] border-white/20 px-3 h-[42px] flex items-center" style={phoneGlassPill}>
          <Plane className="w-3.5 h-3.5 text-white/60 mr-2.5 rotate-45 flex-shrink-0" />
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 'auto' }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="text-[12px] text-white/95 font-normal overflow-hidden whitespace-nowrap"
          >
            Lisbon (LIS)
          </motion.div>
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 0.8, repeat: 1 }}
            className="w-px h-4 bg-white/70 ml-0.5"
          />
        </div>
        {/* Dates */}
        <div className="grid grid-cols-2 gap-2">
          <div className="relative rounded-2xl backdrop-blur-md border-[0.5px] border-white/20 px-3 h-[42px] flex items-center" style={phoneGlassPill}>
            <CalendarIcon className="w-3 h-3 text-white/55 mr-2 flex-shrink-0" />
            <div className="text-[11px] text-white/95 font-normal">Jun 14</div>
          </div>
          <div className="relative rounded-2xl backdrop-blur-md border-[0.5px] border-white/20 px-3 h-[42px] flex items-center" style={phoneGlassPill}>
            <CalendarIcon className="w-3 h-3 text-white/55 mr-2 flex-shrink-0" />
            <div className="text-[11px] text-white/95 font-normal">Jun 28</div>
          </div>
        </div>
        {/* Passengers + cabin row */}
        <div className="grid grid-cols-2 gap-2">
          <div className="relative rounded-2xl backdrop-blur-md border-[0.5px] border-white/20 px-3 h-[42px] flex items-center" style={phoneGlassPill}>
            <Users className="w-3 h-3 text-white/55 mr-2 flex-shrink-0" />
            <div className="text-[11px] text-white/95 font-normal">1 adult</div>
          </div>
          <div className="relative rounded-2xl backdrop-blur-md border-[0.5px] border-white/20 px-3 h-[42px] flex items-center" style={phoneGlassPill}>
            <Armchair className="w-3 h-3 text-white/55 mr-2 flex-shrink-0" />
            <div className="text-[11px] text-white/95 font-normal">Economy</div>
          </div>
        </div>
      </div>
      {/* Search CTA — glass pill, not blue */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.4 }}
        className="mt-auto"
      >
        <div
          className="relative rounded-full backdrop-blur-md border-[0.5px] border-white/20 py-3.5 flex items-center justify-center gap-2 text-white text-[12px] font-medium"
          style={phoneGlassPill}
        >
          <Search className="w-3.5 h-3.5" />
          Search
        </div>
      </motion.div>
    </motion.div>
  );
}

function PhoneResults() {
  return (
    <motion.div {...phoneScreenAnim} className="h-full flex flex-col px-4 pt-2 pb-10">
      {/* Route header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] text-white/95 font-medium">SFO → LIS</div>
          <div className="text-[9px] text-white/45">Jun 14 · 1 adult</div>
        </div>
        <div className="text-[9px] uppercase tracking-wider text-white/40 px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03]">
          12 flights
        </div>
      </div>

      <div className="space-y-2 flex-1 overflow-hidden">
        {[
          { air: 'TAP Portugal', dep: '7:55p', arr: '3:20p', dur: '11h 25m', price: '612', stops: '1 stop' },
          { air: 'United', dep: '4:35p', arr: '11:50a', dur: '12h 15m', price: '684', stops: 'Nonstop', selected: true },
          { air: 'Lufthansa', dep: '9:10p', arr: '6:45p', dur: '13h 35m', price: '702', stops: '1 stop' },
        ].map((f, i) => (
          <motion.div
            key={f.air}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.4 }}
            className="relative rounded-2xl overflow-hidden backdrop-blur-md px-3 py-2.5"
            style={{
              ...phoneGlassPill,
              border: f.selected ? '0.5px solid rgba(255,255,255,0.35)' : '0.5px solid rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                  <Plane className="w-2 h-2 text-white/70" />
                </div>
                <span className="text-[10px] font-normal text-white/65">{f.air}</span>
              </div>
              <div className="text-right">
                <div className="text-[13px] font-normal text-white tabular-nums">${f.price}</div>
                <div className="text-[8px] text-white/45 -mt-0.5">total</div>
              </div>
            </div>
            <div className="flex items-baseline justify-between">
              <div className="flex items-baseline gap-1.5">
                <div className="text-[13px] text-white font-normal tabular-nums">{f.dep}</div>
                <div className="text-[10px] text-white/35">→</div>
                <div className="text-[13px] text-white font-normal tabular-nums">{f.arr}</div>
              </div>
              <div className="text-[9px] text-white/50">{f.dur} · {f.stops}</div>
            </div>
            {f.selected && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.7, type: 'spring', stiffness: 260 }}
                className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/15 border border-white/35 flex items-center justify-center backdrop-blur-md"
              >
                <Check className="w-3 h-3 text-white" strokeWidth={2.5} />
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

function PhoneSeats() {
  // Mini cabin map: 4 rows × A B C | D E F. 'x' = taken, '$' = paid, '*' = the
  // seat the animation "picks".
  const rows = [
    { n: 12, seats: ['x', '$', '$', '$', 'x', '$'] },
    { n: 13, seats: ['$', 'x', '$', '*', '$', 'x'] },
    { n: 14, seats: ['$', '$', 'x', '$', 'x', '$'] },
    { n: 15, seats: ['x', '$', '$', '$', '$', 'x'] },
  ];
  const letters = ['A', 'B', 'C', 'D', 'E', 'F'];

  const seatStyle = (s) =>
    s === 'x'
      ? { background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.08)' }
      : { background: 'rgba(122,138,191,0.16)', border: '0.5px solid rgba(122,138,191,0.40)' };

  return (
    <motion.div {...phoneScreenAnim} className="h-full flex flex-col px-4 pt-2 pb-10">
      <div className="mb-2.5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-normal mb-0.5">Pick your seat</div>
          <div className="text-[13px] text-white font-normal">SFO → LIS · United</div>
        </div>
        <div className="text-[9px] uppercase tracking-wider text-white/40 px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03]">
          Live map
        </div>
      </div>

      {/* Cabin */}
      <div className="flex-1 rounded-2xl backdrop-blur-md border-[0.5px] border-white/15 px-3 pt-2.5 pb-2 overflow-hidden" style={phoneGlassPill}>
        {/* Letter header */}
        <div className="flex items-center justify-center gap-1.5 mb-2">
          <div className="w-5" />
          {letters.map((l, i) => (
            <React.Fragment key={l}>
              {i === 3 && <div className="w-4" />}
              <div className="w-7 text-center text-[8px] text-white/35 font-medium">{l}</div>
            </React.Fragment>
          ))}
        </div>
        <div className="space-y-1.5">
          {rows.map((row, rIdx) => (
            <motion.div
              key={row.n}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + rIdx * 0.08, duration: 0.35 }}
              className="flex items-center justify-center gap-1.5"
            >
              <div className="w-5 text-[8px] text-white/35 text-right tabular-nums">{row.n}</div>
              {row.seats.map((s, i) => (
                <React.Fragment key={i}>
                  {i === 3 && <div className="w-4" />}
                  {s === '*' ? (
                    <motion.div
                      initial={{ scale: 1 }}
                      animate={{ scale: [1, 1.18, 1] }}
                      transition={{ delay: 0.8, duration: 0.45, ease: 'easeOut' }}
                      className="w-7 h-7 rounded-md flex items-center justify-center bg-green-400"
                      style={{ boxShadow: '0 0 14px -2px rgba(74,222,128,0.7)' }}
                    >
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 1.0, type: 'spring', stiffness: 300 }}
                      >
                        <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                      </motion.div>
                    </motion.div>
                  ) : (
                    <div className="w-7 h-7 rounded-md" style={seatStyle(s)} />
                  )}
                </React.Fragment>
              ))}
            </motion.div>
          ))}
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-3 mt-2.5 text-[8px] text-white/45">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-[3px]" style={{ background: 'rgba(122,138,191,0.45)' }} />
            Available
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-[3px] bg-white/10" />
            Taken
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-[3px] bg-green-400" />
            Yours
          </span>
        </div>
      </div>

      {/* Selected seat summary */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.4 }}
        className="mt-2.5 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Armchair className="w-3.5 h-3.5 text-green-400" />
          <div>
            <div className="text-[11px] text-white font-normal">Seat 13D · Aisle</div>
            <div className="text-[8px] text-white/45">Added to your payment plan</div>
          </div>
        </div>
        <div className="text-[13px] text-white font-normal tabular-nums">+$24</div>
      </motion.div>
    </motion.div>
  );
}

function PhonePlan() {
  return (
    <motion.div {...phoneScreenAnim} className="h-full flex flex-col px-4 pt-2 pb-10">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-normal mb-0.5">Payment plan</div>
        <div className="text-[14px] text-white font-normal">$684 split into 8 payments</div>
      </div>

      {/* Deposit pills — matches PaymentSchedule */}
      <div className="mb-3">
        <div className="text-[9px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Deposit</div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { v: '15%', selected: true },
            { v: '20%' },
            { v: '25%' },
          ].map((d) => (
            <div
              key={d.v}
              className={`rounded-full text-center py-1.5 text-[10px] font-medium tabular-nums transition-colors ${
                d.selected ? 'bg-green-400 text-black' : 'bg-white/10 text-white/75'
              }`}
            >
              {d.v}
            </div>
          ))}
        </div>
      </div>

      {/* Weekly / Bi-weekly — exact app pattern */}
      <div className="mb-3">
        <div className="text-[9px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Cadence</div>
        <div className="grid grid-cols-2 gap-1.5">
          <div className="rounded-full bg-white/10 text-white/75 text-center py-2 text-[10px] font-medium">
            <div>Weekly</div>
            <div className="text-[8px] text-white/45 tabular-nums">$36.30 × 16</div>
          </div>
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 220 }}
            className="rounded-full bg-green-400 text-black text-center py-2 text-[10px] font-medium relative"
          >
            <div className="flex items-center justify-center gap-1">
              Bi-weekly
              <CheckCircle className="w-2.5 h-2.5" />
            </div>
            <div className="text-[8px] text-black/65 tabular-nums">$83.20 × 8</div>
          </motion.div>
        </div>
      </div>

      {/* Schedule — rounded-full rows like the app */}
      <div className="flex-1 space-y-1 overflow-hidden">
        {[
          { d: 'Today', a: '$102.60', deposit: true },
          { d: 'Jun 26', a: '$83.20' },
          { d: 'Jul 10', a: '$83.20' },
          { d: 'Jul 24', a: '$83.20' },
        ].map((p, i) => (
          <motion.div
            key={p.d}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + i * 0.07, duration: 0.3 }}
            className="flex items-center justify-between py-1.5 px-3 rounded-full bg-white/5 text-[10px]"
          >
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-2.5 h-2.5 text-white/40" />
              <span className="text-white/80 font-normal">{p.d}</span>
              {p.deposit && (
                <span className="text-[8px] uppercase tracking-wider px-1 py-px rounded text-white/55 bg-white/10">Today</span>
              )}
            </div>
            <span className="text-white font-medium tabular-nums">{p.a}</span>
          </motion.div>
        ))}
        <div className="text-[8px] text-white/35 text-center pt-1">+ 4 more payments</div>
      </div>
    </motion.div>
  );
}

function PhonePay() {
  return (
    <motion.div {...phoneScreenAnim} className="h-full flex flex-col px-4 pt-2 pb-10">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-normal mb-0.5">Checkout</div>
        <div className="text-[14px] text-white font-normal">Review & pay</div>
      </div>

      {/* Booking summary card */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-3 mb-3 space-y-1.5">
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-white/55">Flight</span>
          <span className="text-white/95 font-normal">SFO → LIS</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-white/55">Total fare</span>
          <span className="text-white/95 font-normal tabular-nums">$684.00</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-white/55">Plan</span>
          <span className="text-white/95 font-normal">8 × bi-weekly</span>
        </div>
        <div className="border-t border-white/10 pt-1.5 flex items-baseline justify-between">
          <span className="text-[10px] text-white/70 font-normal">Due today</span>
          <span className="text-[16px] text-white font-normal tabular-nums">$102.60</span>
        </div>
      </div>

      {/* Apple Pay button — Stripe dark theme, h-12 like the real app */}
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative h-12 rounded-[10px] overflow-hidden flex items-center justify-center gap-1 bg-black"
        style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.10)' }}
      >
        <motion.div
          aria-hidden="true"
          initial={{ x: '-100%' }}
          animate={{ x: '200%' }}
          transition={{ delay: 0.5, duration: 1.1, ease: 'easeInOut' }}
          className="absolute inset-y-0 w-1/3"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)' }}
        />
        <svg width="14" height="17" viewBox="0 0 384 512" className="text-white" fill="currentColor">
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
        </svg>
        <span className="text-white text-[13px]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro", sans-serif' }}>
          Pay
        </span>
      </motion.div>

      {/* Divider — matches real checkout */}
      <div className="flex items-center gap-2 my-3">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[8px] uppercase tracking-[0.2em] text-white/40">Or pay with card</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Saved card — amber selected per the actual DeferaZeroCheckoutForm */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="rounded-xl px-3 py-2 mb-3 flex items-center gap-2"
        style={{ background: 'rgba(10, 18, 51,0.15)', border: '1px solid rgba(10, 18, 51,0.5)' }}
      >
        <CreditCard className="w-3.5 h-3.5" style={{ color: '#4E6199' }} />
        <span className="text-[10px] text-white/90 font-normal">Visa</span>
        <span className="text-[10px] text-white/55 font-mono tabular-nums">•••• 4242</span>
        <div className="ml-auto w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(10, 18, 51,0.30)' }}>
          <Check className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />
        </div>
      </motion.div>

      {/* Confirm CTA — green-400 like real Defera Capital / Slice */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        className="mt-auto"
      >
        <div className="rounded-full bg-green-400 text-black text-[12px] font-medium py-3 flex items-center justify-center gap-1.5">
          <Zap className="w-3 h-3" />
          Confirm Booking
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.4 }}
        className="mt-2 flex items-center justify-center gap-1.5 text-[9px] text-white/40"
      >
        <Check className="w-2.5 h-2.5 text-green-400" strokeWidth={2.5} />
        Price locked. Confirmation sent.
      </motion.div>
    </motion.div>
  );
}

export default function IndexMinimal() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [messageCycle, setMessageCycle] = useState(0);

  // Password reset: Supabase may redirect to Site URL (/) when redirectTo isn't allowlisted.
  // Redirect to /auth/callback with tokens so recovery can be processed.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const fromHash = hash ? new URLSearchParams(hash.substring(1)) : null;
    const fromSearch = search ? new URLSearchParams(search.slice(1)) : null;
    const type = (fromHash?.get('type') || fromSearch?.get('type')) ?? null;
    const accessToken = (fromHash?.get('access_token') || fromSearch?.get('access_token')) ?? null;
    const tokenHash = (fromHash?.get('token_hash') || fromSearch?.get('token_hash')) ?? null;
    const hasRecovery = type === 'recovery' && (accessToken || tokenHash);
    if (hasRecovery) {
      const suffix = search + (hash || '');
      window.location.replace('/auth/callback' + suffix);
      return;
    }
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const checkUser = () => {
      try {
        const storedUser = localStorage.getItem('defera.user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setCurrentUser(userData);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error checking user:', error);
        setCurrentUser(null);
      }
    };

    checkUser();
    // Listen for storage changes (when user logs in/out in another tab)
    window.addEventListener('storage', checkUser);
    return () => window.removeEventListener('storage', checkUser);
  }, []);

  /**
   * Open the app as a signed-in admin and you land on the inquiry queue, not the
   * marketing page. The PWA's start_url is "/", so without this an admin's first
   * tap of the day lands on the customer pitch and takes two more taps to reach
   * the backlog.
   *
   * ONCE PER SESSION, deliberately. An admin who navigates back to "/" later has
   * asked to see the marketing page, and bouncing them every time would make it
   * unreachable. sessionStorage is the right lifetime for that: it resets when
   * the tab closes, which is exactly what "first visit" means here.
   *
   * `replace` so it never traps them behind the back button.
   */
  useEffect(() => {
    if (!currentUser) return;
    const landing = landingUrlFor(currentUser);
    if (landing === createPageUrl('Home')) return; // not an admin — leave them be
    try {
      if (sessionStorage.getItem(ADMIN_LANDED_KEY)) return;
      sessionStorage.setItem(ADMIN_LANDED_KEY, '1');
    } catch {
      // Private mode or storage disabled: skip the redirect rather than loop.
      return;
    }
    navigate(landing, { replace: true });
  }, [currentUser, navigate]);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [tripMode, setTripMode] = useState('flight');
  
  // Save origin to localStorage whenever it changes
  useEffect(() => {
    if (origin && origin.trim()) {
      localStorage.setItem('lastSearchOrigin', origin.trim());
    }
  }, [origin]);
  const [date, setDate] = useState({ from: undefined, to: undefined });
  // Opens at the party this customer usually searches for (usualTravelers.js).
  const [passengers, setPassengers] = useState(initialSearchTravelers);
  const [cabinClass, setCabinClass] = useState('economy');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { isTravelersOpen, onTravelersOpenChange, earlySearchDelay } = useSearchPickers({ isCalendarOpen });
  const [numberOfMonths, setNumberOfMonths] = useState(2);
  const [isMobile, setIsMobile] = useState(false);

  // Start the search while the visitor is still here: once the widget
  // holds a complete search (and the date picker is closed), it runs
  // quietly in the background and the results page picks it up when they
  // press Search (src/lib/searchPrefetch.js). While the travelers picker is
  // open it waits; closing it, or closing the date picker, starts the search
  // at once (useSearchPickers). Passengers and cabin are
  // shaped the way the results page reads them back from the URL. Filling
  // it in also re-checks for a newer Defera, which Search would then load
  // (src/lib/searchNavigation.js).
  useEffect(() => {
    checkBuildWhileSearching();
    if (isCalendarOpen || isTravelersOpen) return undefined;
    const params = buildFlightSearchParams({
      origin, destination, from: date?.from, to: date?.to, passengers,
      cabinClass: normalizeCabinClass(cabinClass), canonicalPassengers: true,
    });
    if (!params) return undefined;
    const timer = setTimeout(() => prefetchFlightSearch(params), earlySearchDelay());
    return () => clearTimeout(timer);
  }, [origin, destination, date?.from, date?.to, passengers, cabinClass, isCalendarOpen, isTravelersOpen, earlySearchDelay]);

  /** Visible month in the date-picker (UTC-first-of-month). Drives
   *  the SearchAPI cheapest-per-date fetch below so we only ask for
   *  the window the customer is looking at. Anchor to today+22 by
   *  default; sync via useEffect below when the picker opens with a
   *  pre-picked date so the fetch window matches what's on-screen. */
  const [calendarViewMonth, setCalendarViewMonth] = useState(() => {
    const anchor = new Date();
    anchor.setDate(anchor.getDate() + 22);
    return new Date(Date.UTC(anchor.getFullYear(), anchor.getMonth(), 1));
  });

  const {
    prices: calendarPrices,
    lowestPrice: calendarLowestPrice,
    isLoading: calendarPricesLoading,
  } = useFlightCalendarPrices({
    origin,
    destination,
    startDate: calendarViewMonth,
    days: 62,
    cabinClass,
    passengers,
    enabled: isCalendarOpen,
    // Always show round-trip prices on the calendar — matches Google
    // Flights' default UX where the date grid assumes a return trip
    // even before the customer has picked one. Trip length uses the
    // customer's actual range when both dates are picked, otherwise
    // falls back to Google's 7-day default so a blank / half-drawn
    // range still hints at realistic round-trip pricing.
    flightType: 'round_trip',
    tripLengthDays: (date?.from instanceof Date && date?.to instanceof Date)
      ? Math.max(1, Math.round((date.to.getTime() - date.from.getTime()) / 86_400_000))
      : 7,
  });

  /** Sync `calendarViewMonth` to the month the picker will actually
   *  render when it opens. `onMonthChange` doesn't fire on mount /
   *  re-open; without this the state persists at its today+22
   *  default and the fetch window drifts off from the visible view. */
  useEffect(() => {
    if (!isCalendarOpen) return;
    const anchor = (date?.from instanceof Date && !isNaN(date.from.getTime()))
      ? date.from
      : (() => { const d = new Date(); d.setDate(d.getDate() + 22); return d; })();
    setCalendarViewMonth(
      new Date(Date.UTC(anchor.getFullYear(), anchor.getMonth(), 1))
    );
  }, [isCalendarOpen, date?.from]);

  // Map section — lazy load on scroll
  const mapSectionRef = useRef(null);
  const mapTriggerRef = useRef(null);
  const [mapVisible, setMapVisible] = useState(false);
  const [mapRoutes, setMapRoutes] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Safe date setter with validation
  const handleDateChange = useCallback((newDate) => {
    try {
      if (!newDate) {
        setDate({ from: undefined, to: undefined });
        return;
      }

      if (typeof newDate === 'object') {
        const validFrom = newDate.from instanceof Date && !isNaN(newDate.from.getTime()) ? newDate.from : undefined;
        const validTo = newDate.to instanceof Date && !isNaN(newDate.to.getTime()) ? newDate.to : undefined;
        
        setDate({
          from: validFrom,
          to: validTo
        });
        return;
      }

      console.warn('Unexpected date value received:', newDate);
    } catch (error) {
      console.error('Error updating date:', error);
    }
  }, []);

  // Safe date formatter
  const formatDate = useCallback((dateValue, formatString) => {
    try {
      if (!dateValue || !(dateValue instanceof Date) || isNaN(dateValue.getTime())) {
        return null;
      }
      return format(dateValue, formatString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  }, []);

  // Responsive calendar months and mobile detection
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setNumberOfMonths(mobile ? 1 : 2);
      setIsMobile(mobile);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Map: fetch the tiny route data immediately on mount (cheap, ~5KB)
  useEffect(() => {
    fetch('/api/public/route-map')
      .then((r) => (r.ok ? r.json() : { routes: [] }))
      .then((data) => {
        setMapRoutes(data.routes || []);
        setMapLoaded(true);
      })
      .catch(() => setMapLoaded(true));
  }, []);

  // Map: trigger chunk import + mount — early if possible, guaranteed if user reaches the map.
  useEffect(() => {
    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setMapVisible(true);
      import('../components/MapViz').catch(() => {});
      cleanup();
    };

    // Early trigger: Triple Dip section (with big margin for head start)
    const earlyObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) trigger();
      },
      { rootMargin: '600px' }
    );
    if (mapTriggerRef.current) earlyObserver.observe(mapTriggerRef.current);

    // Guaranteed trigger: the map section itself
    const fallbackObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) trigger();
      },
      { rootMargin: '800px' }
    );
    if (mapSectionRef.current) fallbackObserver.observe(mapSectionRef.current);

    // Extra fallback: scroll position threshold
    const onScroll = () => {
      const doc = document.documentElement;
      const pct = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1);
      if (pct > 0.4) trigger();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    function cleanup() {
      earlyObserver.disconnect();
      fallbackObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
    }
    return cleanup;
  }, []);

  // Optimized live search activity animation
  const [currentSearch, setCurrentSearch] = useState('');
  const timeoutRef = useRef(null);
  const conciergeSectionRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState('https://www.youtube.com/embed/U_y1uuQ2f_s?mute=1&loop=1&playlist=U_y1uuQ2f_s&controls=0&modestbranding=1&rel=0&iv_load_policy=3');
  const isVisibleRef = useRef(true);
  const prefersReducedMotion = useRef(false);

  // On-demand generator - uses shared data (200+ names × 26 initials × 250+ destinations = 1.3M+ combos)

  // Detect reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mediaQuery.matches;
    
    const handleChange = (e) => {
      prefersReducedMotion.current = e.matches;
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // The background video under the hero is the heaviest thing on the page
  // (8 MB). It used to load, in both its phone and desktop copies, the moment
  // the page opened, so it competed with everything above it on a slow
  // connection. Now only the copy the screen shows loads, once the visitor
  // scrolls to it; the poster shows until then.
  const heroVideoSectionRef = useRef(null);
  useEffect(() => {
    const section = heroVideoSectionRef.current;
    if (!section) return undefined;
    const start = () => {
      const video = [document.getElementById('hero-video-mobile-index'), document.getElementById('hero-video-index')]
        .find((v) => v && v.getClientRects().length > 0);
      if (!video) return;
      video.preload = 'auto';
      video.play().catch((err) => console.warn('Video autoplay failed:', err));
    };
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return undefined;
    }
    // A fifth of it on screen: on a phone the section starts exactly at the
    // bottom edge, and an element merely touching the edge counts as
    // intersecting at threshold 0.
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        start();
      }
    }, { threshold: 0.2 });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Animate message bubbles once when section is visible
  useEffect(() => {
    if (!conciergeSectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Trigger animation sequence once
            setTimeout(() => setMessageCycle(1), 300);
            setTimeout(() => setMessageCycle(2), 1500);
            setTimeout(() => setMessageCycle(3), 2700);
            setTimeout(() => setMessageCycle(4), 3900);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(conciergeSectionRef.current);

    return () => {
      if (conciergeSectionRef.current) {
        observer.unobserve(conciergeSectionRef.current);
      }
    };
  }, []);

  // Start video when Concierge section enters viewport
  useEffect(() => {
    if (!conciergeSectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && window.innerWidth < 768) {
            // Start video playback when section is visible on mobile
            setVideoSrc('https://www.youtube.com/embed/U_y1uuQ2f_s?autoplay=1&mute=1&loop=1&playlist=U_y1uuQ2f_s&controls=0&modestbranding=1&rel=0&iv_load_policy=3');
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );

    observer.observe(conciergeSectionRef.current);

    return () => {
      if (conciergeSectionRef.current) {
        observer.unobserve(conciergeSectionRef.current);
      }
    };
  }, []);

  // Animation loop with proper cleanup and adaptive throttling
  useEffect(() => {
    // Set initial search text
    setCurrentSearch(generateSearchActivity());

    const scheduleNextUpdate = () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Skip animation if reduced motion is preferred or not visible
      if (prefersReducedMotion.current || !isVisibleRef.current) {
        return;
      }

      // Adaptive delay: longer on mobile to reduce GPU load
      const isMobileDevice = window.innerWidth < 768;
      const rand = Math.random();
      let delay;
      
      if (isMobileDevice) {
        // Slower cadence on mobile
        if (rand < 0.3) {
          delay = 3000 + Math.random() * 1000;
        } else if (rand < 0.6) {
          delay = 5000 + Math.random() * 2000;
        } else {
          delay = 7000 + Math.random() * 3000;
        }
      } else {
        // Faster on desktop
        if (rand < 0.25) {
          delay = 1000 + Math.random() * 500;
        } else if (rand < 0.5) {
          delay = 2000 + Math.random() * 1000;
        } else if (rand < 0.75) {
          delay = 4000 + Math.random() * 2000;
        } else {
          delay = 6000 + Math.random() * 2000;
        }
      }
      
      timeoutRef.current = setTimeout(() => {
        setCurrentSearch(generateSearchActivity());
        scheduleNextUpdate();
      }, delay);
    };
    
    scheduleNextUpdate();
    
    // Cleanup function to prevent memory leaks
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [generateSearchActivity]);

  const handleSearch = () => {
    if (!origin || !destination || !date.from) {
      return;
    }

    // Save origin to localStorage for Explore page and Home page
    localStorage.setItem('lastSearchOrigin', origin);

    // Enforce 22-day minimum booking requirement (day-based comparison)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 22);
    
    const departureDate = new Date(date.from);
    departureDate.setHours(0, 0, 0, 0);
    
    if (departureDate < minDate) {
      alert('Flights must be booked at least 22 days in advance');
      return;
    }
    
    const originCode = typeof origin === 'string' ? origin : origin.code || origin;
    const destinationCode = typeof destination === 'string' ? destination : destination.code || destination;
    
    // Build query params (match GoogleFlightsSearch param names)
    const params = new URLSearchParams({
      origin: originCode,
      destination: destinationCode,
      departureDate: format(date.from, 'yyyy-MM-dd'),
      cabin: cabinClass,
      tripMode: tripMode
    });
    writePassengerParams(params, passengers);

    // Add return date if selected
    if (date.to) {
      params.append('returnDate', format(date.to, 'yyyy-MM-dd'));
    }
    
    openSearchResults(navigate, `/search?${params.toString()}`);
  };

  return (
    <>
      <PageSeo path="/" />
      <MarketingTags googleAds contentsquare />
      
      <div
        className="min-h-screen w-full flex flex-col items-center p-6 pt-2 md:pt-6 pb-12 md:pb-6 relative md:justify-center"
        style={{
          /* Black into deep teal into pale sky, stops sampled from the Grainient "Hero V3"
             reference (2026-09-27). The pale end meets the video below, as the old one did. */
          background: 'linear-gradient(to bottom, #000000 0%, #000000 36%, #010006 40%, #01000d 45%, #00031a 50%, #000e29 55%, #011b36 60%, #012541 64%, #013250 68%, #104662 72%, #2b5e79 76%, #4b7a90 80%, #6b94a8 84%, #89adbf 88%, #a6c6d5 92%, #bfd9e4 96%, #cce2ed 100%)',
        }}
      >
        {/* Navigation buttons - All in one row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="absolute top-5 left-4 right-4 md:top-6 md:left-6 md:right-6 flex items-center justify-between"
        >
          {/* Home button - Left side */}
          <button
            onClick={() => {
              if (currentUser) {
                // If logged in, go to home page
                navigate(createPageUrl('Home'));
              } else {
                // If not logged in, stay on index page
                navigate('/');
              }
            }}
            className="text-white/35 hover:text-white/75 text-[11px] md:text-[12px] font-normal uppercase tracking-[0.2em] transition-colors"
          >
            Home
          </button>

          {/* How it Works and Sign Up - Right side */}
          <div className="flex items-center gap-6 sm:gap-7">
            <Link
              to="/how-it-works"
              className="text-white/35 hover:text-white/75 text-[11px] md:text-[12px] font-medium uppercase tracking-[0.2em] transition-colors"
            >
              How it Works
            </Link>
            <button
              onClick={() => currentUser ? navigate('/trips') : navigate('/home?auth_mode=login')}
              className="text-white/35 hover:text-white/75 text-[11px] md:text-[12px] font-medium uppercase tracking-[0.2em] transition-colors"
            >
              Login
            </button>
          </div>
        </motion.div>

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mt-[15vh] md:mt-0 mb-2"
      >
        <img
          src="/defera-logo-web.png"
          alt="Defera Logo"
          className="h-14 md:h-[4.25rem] mx-auto"
        />
      </motion.div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-center mb-12"
      >
        {/* The page's one h1: the phrase people search for. Preflight resets h1 sizing, so it looks as before. */}
        <h1 className="text-white/60 text-sm md:text-base font-normal leading-snug">
          Book flights in installments.
        </h1>
        <p className="text-white/40 text-xs md:text-sm font-normal leading-snug">
          No credit check. No interest.
        </p>
      </motion.div>


      {/* Search Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="w-full max-w-3xl"
      >
        <div className="space-y-4">
          {/* Trip Mode Toggle */}
          <div className="flex flex-nowrap gap-2 mb-2">
            <button
              type="button"
              onClick={() => setTripMode('flight')}
              className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-2xl font-normal text-sm transition-all duration-300 ${
                tripMode === 'flight' || tripMode === 'flight_stay'
                  ? 'bg-white text-black'
                  : 'backdrop-blur-md border-[0.5px] border-white/20 text-white/90 hover:text-white bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]'
              }`}
            >
              Flights
            </button>
            <button
              type="button"
              onClick={() => setTripMode(tripMode === 'flight_stay' ? 'flight' : 'flight_stay')}
              className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-2xl font-normal text-sm transition-all duration-300 ${
                tripMode === 'flight_stay'
                  ? 'bg-white text-black'
                  : 'backdrop-blur-md border-[0.5px] border-white/20 text-white/90 hover:text-white bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]'
              }`}
            >
              Stays
            </button>
            <button
              type="button"
              onClick={() => navigate('/explore')}
              className="flex-1 md:flex-none md:px-5 py-1.5 rounded-2xl font-normal text-sm transition-all duration-300 backdrop-blur-md border-[0.5px] border-white/20 text-white/90 hover:text-white bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
            >
              Explore
            </button>
          </div>
          {/* From and To - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            <AirportInput
              value={origin}
              onChange={setOrigin}
              placeholder="From"
              inputClassName="backdrop-blur-md border-[0.5px] border-white/20 text-white placeholder:text-white/60 rounded-2xl pl-12 pr-4 py-2 text-base font-normal transition-all duration-300 h-[42px] bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
              iconClassName="left-4 w-5 h-5 text-white/60 z-10"
            />

            <AirportInput
              value={destination}
              onChange={setDestination}
              placeholder="To"
              inputClassName="backdrop-blur-md border-[0.5px] border-white/20 text-white placeholder:text-white/60 rounded-2xl pl-12 pr-4 py-2 text-base font-normal transition-all duration-300 h-[42px] bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
              iconClassName="left-4 w-5 h-5 text-white/60 z-10"
            />
          </div>

          {/* Dates, Passengers, and Cabin - Grid */}
          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-4">
            {/* Dates - Full width on mobile, 1/3 on desktop */}
            <div className="md:col-span-1">
              {isMobile ? (
                // Mobile: Use Dialog for full-screen centered calendar
                <Dialog open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative w-full backdrop-blur-md border-[0.5px] border-white/20 text-white rounded-2xl pl-12 pr-4 py-2 h-[42px] justify-start text-base font-normal transition-all duration-300 bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
                    >
                      <CalendarIcon className="absolute left-4 w-5 h-5 text-white/60" />
                      {date?.from ? date.to ? `${formatDate(date.from, "MMM d") || 'Date'} - ${formatDate(date.to, "MMM d") || 'Date'}` : formatDate(date.from, "MMM d, yyyy") || 'Date' : <span className="text-white/40">Dates</span>}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-2xl bg-brand-950/95 border-brand-800/45 backdrop-blur-2xl max-w-[95vw] p-0 [&>button]:hidden">
                    <CalendarRangeStyles />
                    <CalendarComponent
                      mode="range"
                      defaultMonth={date?.from || getMinBookableDate()}
                      selected={date}
                      onSelect={handleDateChange}
                      onMonthChange={(m) => setCalendarViewMonth(new Date(Date.UTC(m.getFullYear(), m.getMonth(), 1)))}
                      numberOfMonths={numberOfMonths}
                     
                      disabled={[{ before: (() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const minDate = new Date(today);
                        minDate.setDate(minDate.getDate() + 22);
                        return minDate;
                      })() }]}
                      className="text-white"
                      dayPrices={calendarPrices}
                      lowestPrice={calendarLowestPrice}
                      isLoading={calendarPricesLoading}
                    />
                    <CalendarDock onDone={() => setIsCalendarOpen(false)} />
                  </DialogContent>
                </Dialog>
              ) : (
                // Desktop: Use Popover for dropdown calendar
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative w-full backdrop-blur-md border-[0.5px] border-white/20 text-white rounded-2xl pl-12 pr-4 py-2 h-[42px] justify-start text-base font-normal transition-all duration-300 bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
                    >
                      <CalendarIcon className="absolute left-4 w-5 h-5 text-white/60" />
                      {date?.from ? date.to ? `${formatDate(date.from, "MMM d") || 'Date'} - ${formatDate(date.to, "MMM d") || 'Date'}` : formatDate(date.from, "MMM d, yyyy") || 'Date' : <span className="text-white/40">Dates</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-auto p-0 rounded-2xl bg-brand-950/95 border-brand-800/45 text-white backdrop-blur-xl shadow-2xl" 
                    align="center" 
                    side="bottom" 
                    sideOffset={8}
                    alignOffset={0}
                    collisionPadding={20}
                    sticky="always"
                  >
                    <CalendarRangeStyles />
                    <CalendarComponent
                      mode="range"
                      defaultMonth={date?.from || getMinBookableDate()}
                      selected={date}
                      onSelect={handleDateChange}
                      onMonthChange={(m) => setCalendarViewMonth(new Date(Date.UTC(m.getFullYear(), m.getMonth(), 1)))}
                      numberOfMonths={numberOfMonths}
                     
                      disabled={[{ before: (() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const minDate = new Date(today);
                        minDate.setDate(minDate.getDate() + 22);
                        return minDate;
                      })() }]}
                      className="text-white"
                      dayPrices={calendarPrices}
                      lowestPrice={calendarLowestPrice}
                      isLoading={calendarPricesLoading}
                    />
                    <CalendarDock onDone={() => setIsCalendarOpen(false)} />
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Passengers & Cabin Class - Same row on mobile, separate columns on desktop */}
            <div className="grid grid-cols-2 gap-4 md:col-span-2 md:grid-cols-2">
              {/* Passengers */}
              <PassengerSelector 
                passengers={passengers} 
                onChange={setPassengers}
                onOpenChange={onTravelersOpenChange}
                className="backdrop-blur-md border-[0.5px] border-white/20 text-white rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal transition-all duration-300 bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
              />

              {/* Cabin Class */}
              <CabinClassSelector 
                value={cabinClass} 
                onChange={setCabinClass}
                className="backdrop-blur-md border-[0.5px] border-white/20 text-white rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal transition-all duration-300 bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]"
              />
            </div>
          </div>

          {/* Search Button */}
          <Button
            variant="ghost"
            onClick={handleSearch}
            disabled={!origin || !destination || !date.from}
            className="w-full backdrop-blur-md border-[0.5px] border-white/20 text-white text-base py-6 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)] disabled:hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] disabled:hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)]"
          >
            Search
          </Button>
        </div>
      </motion.div>

      {/* Live Search Activity Animation - Optimized for Mobile */}
      {/* Not in the iOS app: the names and places are generated, not real searches. */}
      {!isNativeApp && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-10 flex justify-center"
      >
        <div className="bg-white/5 backdrop-blur-[3px] border border-white/10 rounded-full px-4 py-2 inline-flex items-center gap-2">
          {/* Green pulse dot - paused if reduced motion */}
          <div 
            className="w-2 h-2 rounded-full bg-green-400"
            style={{
              animation: prefersReducedMotion.current ? 'none' : 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          ></div>
          {/* Transform-only animation with will-change hint */}
          <AnimatePresence mode="wait">
            <motion.p
              key={currentSearch}
              initial={{ opacity: 0, transform: 'translate3d(0, 10px, 0)' }}
              animate={{ opacity: 1, transform: 'translate3d(0, 0, 0)' }}
              exit={{ opacity: 0, transform: 'translate3d(0, -10px, 0)' }}
              transition={{ 
                duration: prefersReducedMotion.current ? 0 : 0.3,
                ease: 'easeOut'
              }}
              className="text-white/70 text-xs md:text-sm font-normal"
              style={{
                willChange: 'transform, opacity'
              }}
            >
              {currentSearch}
            </motion.p>
          </AnimatePresence>
        </div>
      </motion.div>
      )}
    </div>

      {/* Hero Video Section - Enhanced with Dynamic Elements */}
      <section ref={heroVideoSectionRef} className="w-full relative mb-0 overflow-hidden">
        <div className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden">

          {/* Mobile version - Video with autoplay */}
          <div className="absolute inset-0 md:hidden bg-black">
            <video
              id="hero-video-mobile-index"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              loop
              muted
              playsInline
              preload="none"
              poster="/hero-poster.jpg"
              src="/hero-video.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none z-10" style={{ backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 60%, transparent 100%)' }} />
          </div>
          
          {/* Desktop version - Video with autoplay */}
          <div className="absolute inset-0 hidden md:block">
            <video
              id="hero-video-index"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              loop
              muted
              playsInline
              preload="none"
              poster="/hero-poster.jpg"
              src="/hero-video.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t pointer-events-none" style={{ backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 60%, transparent 100%)' }} />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* REDESIGNED SECTIONS - APPLE STYLE */}
      {/* ============================================ */}

      <div className="bg-[#fafafa] text-gray-900 font-sans">
        

        {/* Main Value Prop (Deposit) — Apple-style dark section, flows out of the hero video */}
        <section className="w-full bg-black py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-white mb-3 md:mb-4">Book now, pay later</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Secure your flight
                  <br />
                  with 0% down.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-white/80 leading-relaxed max-w-xl mx-auto">
                Lock in today's price with no large deposits. Pay the rest over time with{' '}
                <span className="font-semibold text-white">zero interest and no credit check</span>.
              </p>
            </motion.div>

            {/* Trust strip — As Seen On + Trustpilot */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 mb-12 md:mb-16"
            >
              <img
                src="/images/as-seen-on.png"
                alt="CBS News, ABC, NBC, FOX"
                className="h-10 md:h-12 opacity-60 grayscale hover:grayscale-0 transition-all"
              />
              <div className="hidden sm:block w-px h-8 bg-white/15" aria-hidden="true" />
              <a
                href="https://www.trustpilot.com/review/defera.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <div className="flex gap-1">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-5 h-5 md:w-6 md:h-6 bg-[#00b67a] flex items-center justify-center rounded-sm">
                      <Star className="w-3 h-3 md:w-4 md:h-4 text-white fill-white" />
                    </div>
                  ))}
                  {/* Half-filled tile for 4.5 rating */}
                  <div className="w-5 h-5 md:w-6 md:h-6 relative flex items-center justify-center rounded-sm overflow-hidden">
                    <div className="absolute inset-0 bg-[#5a5f6d]" />
                    <div className="absolute inset-y-0 left-0 w-1/2 bg-[#00b67a]" />
                    <Star className="relative w-3 h-3 md:w-4 md:h-4 text-white fill-white" />
                  </div>
                </div>
                <span className="text-white/90 text-base font-semibold whitespace-nowrap group-hover:text-brand-200 transition-colors">4.5 Trustpilot</span>
              </a>
            </motion.div>

            {/* Bento grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {/* Main tile — No credit checks */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                className="relative md:row-span-2 rounded-[28px] bg-[#161617] overflow-hidden p-7 sm:p-10 flex flex-col"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-40 -right-40 w-[28rem] h-[28rem]"
                  style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.28), transparent 65%)' }}
                />
                <h3 className="relative text-[1.75rem] sm:text-[2.125rem] font-semibold tracking-tight text-white leading-[1.12] mb-3">
                  No credit checks.
                  <br />
                  Everyone is approved.
                </h3>
                <p className="relative text-[15px] md:text-base text-white/80 leading-relaxed mb-8 md:mb-10">
                  Your credit score never enters the picture — booking with Defera is open to everyone, instantly.
                </p>
                <ul className="relative space-y-4 mt-auto">
                  {[
                    'Instant approval',
                    'No hard or soft inquiries',
                    'No credit limits',
                    'Everyone qualifies automatically',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3.5 text-[15px] md:text-base text-white/85">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.08] flex-shrink-0">
                        <Check className="w-4 h-4 text-emerald-400" strokeWidth={2} strokeLinecap="round" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Tile — Zero interest */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 0.61, 0.36, 1] }}
                className="rounded-[28px] bg-[#161617] p-7 sm:p-10"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                  <Percent className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                </span>
                <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-white leading-tight mb-2">
                  Zero interest.
                </h3>
                <p className="text-[15px] md:text-base text-white/80 leading-relaxed">
                  The price you see is exactly what you pay. No hidden charges, no surprises.
                </p>
              </motion.div>

              {/* Tile — Price lock */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
                className="rounded-[28px] bg-[#161617] p-7 sm:p-10"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                  <Lock className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                </span>
                <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-white leading-tight mb-2">
                  Lock in today's price.
                </h3>
                <p className="text-[15px] md:text-base text-white/80 leading-relaxed">
                  Flight prices go up — yours won't. Secure your fare now with no large deposits.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Beauty of Freedom - Emotional hook for conversion */}
        <motion.section
          className="w-full py-28 md:py-32 px-6 bg-black"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
          transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-[2.6rem] md:text-6xl font-semibold tracking-tight leading-[1.05] mb-6 md:mb-8 text-center">
                <span className="text-white">
                  The Beauty of Freedom
                </span>
              </h2>
              <p className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto leading-snug md:leading-normal">
                Travel is more than movement—it's the art of expansion. Each journey unlocks new perspectives,
                transforms your understanding of the world, and reveals the infinite possibilities that exist
                beyond familiar horizons. Freedom to explore is freedom to become.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
              className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 shadow-2xl"
            >
              <BeautyOfFreedomVideo />
            </motion.div>
          </div>
        </motion.section>

        {/* Editorial lifestyle narrative — adapted from the "Welcome to Defera" email.
             Intro and closing stay constrained for readability. The three lifestyle
             cards are contained/rounded on mobile and go FULL-BLEED (edge to edge)
             on desktop so they read like a magazine spread between the light
             Concierge section above and the dark "Ready to book?" CTA below. */}
        <section className="w-full py-24 md:py-28 bg-black">
          {/* Section intro — constrained container */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
            className="max-w-5xl mx-auto px-6 mb-14 md:mb-20"
          >
            <div className="mb-4">
              <span className="text-[11px] uppercase tracking-[0.22em] font-normal text-brand-300">
                You have been meaning to do this
              </span>
            </div>
            <h2 className="text-[2.6rem] md:text-6xl font-semibold tracking-tight leading-[1.05] text-white">
              The trip you have<br className="hidden md:block" /> been dreaming about.
            </h2>
            <p className="mt-5 text-white/60 text-base md:text-lg font-light leading-snug md:leading-relaxed max-w-xl">
              It is finally within reach. Book any flight in the world today, and pay for it in comfortable installments over the coming weeks or months. No interest. No credit check. Just the trip you have been waiting for.
            </p>
          </motion.div>

          {/* Cards wrapper — edge-to-edge on all viewports for a continuous
               magazine-spread feel. Cards butt up against each other at every
               breakpoint. */}
          <div className="space-y-0">
            {/* Card 1 — Destinations aspiration.
                 md:grid-cols-2 + md:items-stretch splits each row into 1:1 columns
                 on desktop; the image fills its column via absolute positioning +
                 object-cover so the image's natural aspect never dictates row height.
                 The inner max-w-md on the text keeps line length readable at any
                 viewport width. */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
              className="overflow-hidden bg-black md:grid md:grid-cols-2 md:items-stretch md:min-h-[420px] lg:min-h-[520px]"
            >
              <div className="relative">
                <img
                  src="/bali-email.jpg"
                  alt="Cliffside ocean view in Bali"
                  width="600"
                  height="900"
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto md:absolute md:inset-0 md:w-full md:h-full md:object-cover"
                />
              </div>
              <div className="px-6 py-14 md:px-14 lg:px-20 md:py-24 md:flex md:flex-col md:justify-center">
                <div className="md:max-w-md">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-normal text-white/40 mb-3">
                    Somewhere worth flying to
                  </div>
                  <h3 className="text-2xl md:text-[2rem] font-semibold tracking-tight leading-[1.1] text-white mb-3">
                    The world just got closer.
                  </h3>
                  <p className="text-white/80 text-[15px] md:text-base font-normal leading-relaxed">
                    Bali. Tokyo. Paris. The destinations you have been dreaming of for months are no longer reserved for someday. Every flight on Defera can be secured today with a small deposit — and paid for comfortably, on your own schedule.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 2 — Travel together. Image right / text left on desktop
                 (md:order-*) to create editorial rhythm between the three cards. */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
              className="overflow-hidden bg-black md:grid md:grid-cols-2 md:items-stretch md:min-h-[420px] lg:min-h-[520px]"
            >
              <div className="relative md:order-2">
                <img
                  src="/travelstock-email.jpg"
                  alt="Couple enjoying a river cruise together"
                  width="600"
                  height="400"
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto md:absolute md:inset-0 md:w-full md:h-full md:object-cover"
                />
              </div>
              <div className="px-6 py-14 md:px-14 lg:px-20 md:py-24 md:order-1 md:flex md:flex-col md:justify-center md:items-end">
                <div className="md:max-w-md">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-normal text-white/40 mb-3">
                    Trips are better together
                  </div>
                  <h3 className="text-2xl md:text-[2rem] font-semibold tracking-tight leading-[1.1] text-white mb-3">
                    Bring who you love.
                  </h3>
                  <p className="text-white/80 text-[15px] md:text-base font-normal leading-relaxed">
                    Book for two, four, or the entire party in a single flow. Everyone can split their own installments, or one of you can cover the whole trip — the choice is yours. What once meant weeks of logistics now begins with a single conversation.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 4 — Premium experience. Image left / text right on desktop. */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
              className="overflow-hidden bg-black md:grid md:grid-cols-2 md:items-stretch md:min-h-[420px] lg:min-h-[520px]"
            >
              <div className="relative">
                <img
                  src="/planeseat-email.jpg"
                  alt="Traveler enjoying a premium cabin"
                  width="600"
                  height="450"
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto md:absolute md:inset-0 md:w-full md:h-full md:object-cover"
                />
              </div>
              <div className="px-6 py-14 md:px-14 lg:px-20 md:py-24 md:flex md:flex-col md:justify-center">
                <div className="md:max-w-md">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-normal text-white/40 mb-3">
                    First class in every sense
                  </div>
                  <h3 className="text-2xl md:text-[2rem] font-semibold tracking-tight leading-[1.1] text-white mb-3">
                    Fly the way you always imagined.
                  </h3>
                  <p className="text-white/80 text-[15px] md:text-base font-normal leading-relaxed">
                    Complimentary upgrades on eligible routes. Concierge support when plans shift. Rewards worth using on your next journey. This is what modern travel should feel like — and what Defera is built to deliver.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

        </section>

        {/* How It Works — 4 steps + animated phone demo */}
        <section className="w-full bg-black py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Section header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-12 md:mb-16"
            >
              <p className="text-sm md:text-base font-medium text-white mb-3 md:mb-4">How it works</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Book in four steps.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-white/80 leading-relaxed max-w-xl mx-auto">
                From search to checkout in{' '}
                <span className="font-semibold text-white">under 60 seconds</span>. No credit check, no interest,
                no surprises.
              </p>
            </motion.div>

            {/* Phone + steps */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Phone animation */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="order-2 lg:order-2"
              >
                <HowItWorksPhone />
              </motion.div>

              {/* Steps */}
              <div className="order-1 lg:order-1 space-y-4 md:space-y-5">
                {[
                  {
                    n: 1,
                    title: 'Search and pick your flight.',
                    desc: 'Real-time fares from every major airline. The price you see is the price you lock in.',
                  },
                  {
                    n: 2,
                    title: 'Pick your seat.',
                    desc: 'Browse a live seat map on select airlines — window, aisle, or extra legroom.',
                  },
                  {
                    n: 3,
                    title: 'Choose your payment plan.',
                    desc: 'Split it weekly or bi-weekly. No credit check, no interest, no late fees.',
                  },
                  {
                    n: 4,
                    title: 'Pay with card or Apple Pay.',
                    desc: 'One tap to confirm. Your trip price is secured, your schedule is set. Done.',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.n}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                    className="flex items-start gap-4 sm:gap-5 rounded-[28px] bg-[#161617] p-6 sm:p-7"
                  >
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white/[0.08] flex-shrink-0 text-brand-200 text-[15px] font-semibold tabular-nums">
                      {step.n}
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <h3 className="text-lg sm:text-xl font-semibold text-white tracking-tight mb-1.5 leading-snug">
                        {step.title}
                      </h3>
                      <p className="text-white/80 text-[15px] md:text-base leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-center mt-12 md:mt-16"
            >
              <button
                onClick={() => navigate('/how-it-works')}
                className="inline-flex items-center gap-1.5 text-brand-200 font-medium text-[15px] md:text-base hover:underline underline-offset-4 transition-colors"
              >
                See the full breakdown
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </section>

        {/* Seat Selection — live seat map on select airlines */}
        <section className="w-full bg-black py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Single column on mobile: text first, seat map after */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left — text content */}
              <motion.div
                className="order-1 min-w-0"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
              >
                <p className="text-sm md:text-base font-medium text-white mb-3 md:mb-4">Seat selection</p>
                <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                  <span
                    style={{
                      background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Your trip, your seat.
                  </span>
                </h2>
                <p className="text-[17px] md:text-lg text-white/80 leading-relaxed mb-6 max-w-lg">
                  On select airlines, browse a live seat map of your exact aircraft and pick the seat you want —
                  window, aisle, or extra legroom.{' '}
                  <span className="font-semibold text-white">No one else in layaway travel offers it.</span>
                </p>

                {/* World's First badge */}
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.08] mb-9 md:mb-10">
                  <Award className="w-3.5 h-3.5 text-brand-200" strokeWidth={1.8} />
                  <span className="text-xs font-medium text-brand-200 tracking-wide">World's first in layaway</span>
                </span>

                <div className="space-y-6 md:space-y-7 mb-10 md:mb-12">
                  {[
                    {
                      icon: Plane,
                      title: 'Live cabin map.',
                      desc: 'See exactly which seats are open on your flight, with per-seat pricing as you browse.',
                    },
                    {
                      icon: CheckCircle2,
                      title: 'Select or purchase in a few taps.',
                      desc: 'Pick your favorite seats, or purchase them if not included in your fare.',
                    },
                    {
                      icon: CreditCard,
                      title: 'Rolled into your installment plan.',
                      desc: 'Seat costs fold right into your existing payment schedule — no separate charge, no surprise fees.',
                    },
                  ].map((item, i) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-60px' }}
                      transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                      className="flex items-start gap-4"
                    >
                      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] flex-shrink-0">
                        <item.icon className="w-[18px] h-[18px] text-brand-200" strokeWidth={1.8} />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-white font-semibold text-[15px] md:text-base mb-1">{item.title}</h3>
                        <p className="text-white/80 text-sm md:text-[15px] leading-relaxed max-w-md">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <button
                    onClick={() => navigate('/search')}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-[#1d1d1f] bg-white hover:bg-white/90 transition-colors w-full sm:w-auto"
                  >
                    Find your flight
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="mt-4 text-[11px] text-white/30 font-normal max-w-sm">
                    Seat selection availability and pricing vary by airline, route, and fare class.
                  </p>
                </motion.div>
              </motion.div>

              {/* Right — seat map screenshot in a dark bento tile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, ease: [0.22, 0.61, 0.36, 1] }}
                className="order-2 relative flex items-center justify-center min-w-0"
              >
                <div className="relative w-full max-w-[360px] sm:max-w-[30rem] mx-auto rounded-[28px] bg-[#161617] px-4 pt-4 sm:px-6 sm:pt-6 overflow-hidden">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-32 -right-32 w-96 h-96"
                    style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.25), transparent 65%)' }}
                  />
                  {/* seats-cutout.png is seats.png with the dark page/card background
                      converted to true alpha transparency, so the seat map UI floats
                      on the tile. Bottom fade keeps the cropped seat rows from
                      hitting the tile edge. Served as a 900px WebP (64 KB) where the
                      browser takes it; the PNG stays as the fallback. */}
                  <picture className="contents">
                    <source srcSet="/seats-cutout-900.webp" type="image/webp" />
                    <img
                      src="/seats-cutout.png"
                      alt="Defera live seat map — select your seats on a real cabin layout"
                      loading="lazy"
                      decoding="async"
                      className="relative w-full object-contain"
                      style={{
                        transform: 'scale(1.08)',
                        transformOrigin: '50% 12%',
                        WebkitMaskImage: 'linear-gradient(to bottom, #000 78%, transparent 92%)',
                        maskImage: 'linear-gradient(to bottom, #000 78%, transparent 92%)',
                      }}
                    />
                  </picture>
                </div>
                {/* Floating seat-confirmation chip over the image */}
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ delay: 0.5, duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
                  className="absolute bottom-[12%] right-[2%] sm:right-[10%] lg:right-0 flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5 bg-[#28282b]/95 backdrop-blur-xl shadow-[0_18px_40px_-12px_rgba(0,0,0,0.8)]"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-[12px] text-white font-medium leading-tight">Seat 21F confirmed</div>
                    <div className="text-[10px] text-white/50 leading-tight">+$24 · added to your plan</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Popular Destinations - Hidden per product decision */}
        {false && (
        <section
          className="w-full py-28 md:py-32 px-6 relative overflow-hidden"
          style={{
            background: 'linear-gradient(to bottom, #0a0a0a 0%, #05091E 15%, #091034 35%, #0F1952 55%, #080E28 70%, #4E6199 82%, #fafafa 92%)',
          }}
        >
          {/* Subtle background decoration */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-400/10 rounded-full blur-3xl -mr-[300px] -mt-[300px] pointer-events-none" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="flex items-end justify-between mb-16">
              <div className="max-w-3xl">
            <h2
                  className="text-[2.6rem] md:text-6xl font-semibold mb-6 tracking-tight leading-[1.05]"
                >
                  <span className="text-white">Trending Destinations</span>
            </h2>
            <p
                  className="text-lg md:text-2xl font-light leading-snug md:leading-normal text-white/80"
                >
                  Book now, pay over time. Spread your trip cost over 6 months.
            </p>
        </div>
              <button 
                type="button"
                onClick={() => navigate('/explore')} 
                className="hidden md:flex items-center gap-2 text-brand-300 hover:text-brand-200 font-semibold transition-colors group text-lg"
              >
                View all destinations <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
          </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                { code: 'CUN', city: 'Cancun', country: 'Mexico', price: 20, img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1000&auto=format&fit=crop' },
                { code: 'LHR', city: 'London', country: 'United Kingdom', price: 25, img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=1000&auto=format&fit=crop' },
                { code: 'TYO', city: 'Tokyo', country: 'Japan', price: 35, img: 'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?q=80&w=1000&auto=format&fit=crop' },
                { code: 'CDG', city: 'Paris', country: 'France', price: 28, img: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=1000&auto=format&fit=crop' },
                { code: 'JFK', city: 'New York', country: 'United States', price: 22, img: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=1000&auto=format&fit=crop' },
                { code: 'DXB', city: 'Dubai', country: 'United Arab Emirates', price: 32, img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1000&auto=format&fit=crop' }
              ].map((dest) => (
              <div
                  key={dest.code}
                  onClick={() => { setDestination(dest.code); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
                  className="group cursor-pointer relative rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-transform duration-300 hover:scale-[1.02]"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    <img src={dest.img} alt={dest.city} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>

                  {/* Content */}
                  <div className="p-5 md:p-6 bg-gradient-to-br from-stone-100 via-stone-200 to-stone-400">
                    <div className="flex items-start justify-between mb-3">
            <div>
                        <h3 className="text-2xl md:text-3xl font-medium mb-1">
                          <span className="text-gray-900">{dest.city}</span>
                        </h3>
                        <p className="text-gray-500 text-sm">{dest.country}</p>
            </div>
                      <div className="text-right">
                        <div className="text-2xl md:text-3xl font-medium text-gray-900">${dest.price}</div>
                        <div className="text-xs text-gray-500">per week</div>
          </div>
                </div>
            </div>
                </div>
              ))}
            </div>
        </div>
      </section>
        )}

        {/* Comparison Section - Defera vs Competitors */}
        <section className="w-full py-28 md:py-32 px-6 relative overflow-hidden">
          {/* Gradient background: off-white at top (matches FAQ), warm tones through middle, black at bottom */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, #fafafa 0%, #D5DAEB 45%, #4E6199 62%, #0F1952 72%, #080E28 82%, #091034 92%, #05091E 97%, #0a0a0a 100%)'
            }}
          />
        <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <motion.h2 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                className="text-[2.6rem] md:text-6xl font-semibold mb-6 tracking-tight leading-[1.05]"
              >
                <span className="text-gray-900">
                  Why we're<br className="md:hidden" /><span className="hidden md:inline"> </span>the best.
                  </span>
              </motion.h2>
              <motion.p 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
                className="text-lg md:text-2xl font-light leading-snug md:leading-normal"
              >
                <span className="text-gray-600">See why travelers choose Defera over other options.</span>
              </motion.p>
                </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
              className="relative bg-white/15 md:backdrop-blur-xl rounded-[2rem] shadow-xl border border-white/15 overflow-hidden p-3 md:p-0"
            >
              {/* Tint overlay - lightens bottom for readability on dark gradient */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/20 pointer-events-none rounded-[2rem]" aria-hidden />
              <div className="grid grid-cols-[2.25fr_0.875fr_0.875fr] md:grid-cols-[2fr_1fr_1fr] relative z-10">
                <div className="px-3 py-2 md:py-4 md:px-6 lg:py-4 lg:px-8 text-[11px] md:text-sm font-medium uppercase tracking-wider leading-none md:leading-normal md:bg-white/15">
                  <span className="text-gray-800">FEATURE</span>
              </div>
                <div className="px-3 py-2 md:py-4 md:px-6 lg:py-4 lg:px-8 text-center text-[11px] md:text-sm font-medium uppercase tracking-wider leading-none md:leading-normal md:bg-green-600/20">
                  <span className="text-green-800">DEFERA</span>
                </div>
                <div className="px-3 py-2 md:py-4 md:px-6 lg:py-4 lg:px-8 text-center text-[11px] md:text-sm font-medium uppercase tracking-wider leading-none md:leading-normal md:bg-white/15">
                  <span className="text-gray-800">OTHERS</span>
            </div>

              {[
                // Mutual features (both Defera and others) - at the top
                { feature: "No Credit Check", defera: true, others: true },
                { feature: "0% Interest", defera: true, others: true },
                { feature: "0% Down Payment", defera: true, others: true },
                { feature: "Fare Price Lock", defera: true, others: true },
                // Defera-only features - ranked by importance
                { feature: "Unlimited Bookings", defera: true, others: false },
                { feature: "Seat Selection", defera: true, others: false },
                { feature: "Airline Credits Accepted", defera: true, others: false },
                { feature: "Airline Gift Cards Accepted", defera: true, others: false },
                { feature: "Flight + Hotel Packages", defera: true, others: false },
                { feature: "Earn Frequent Flyer Miles", defera: true, others: false },
                { feature: "Earn Credit Card Points", defera: true, others: false },
                { feature: "Earn Defera Miles", defera: true, others: false },
                { feature: "Redeem Miles at 200+ Brands", defera: true, others: false },
                { feature: "Book JSX Flights", defera: true, others: false, logo: "https://www.jsx.com/favicon.ico" },
                { feature: "Book Southwest Flights", defera: true, others: false, logo: "https://www.southwest.com/favicon.ico" },
                { feature: "Complimentary Upgrades", defera: true, others: false },
                { feature: "World-Class Concierge", defera: true, others: false },
              ].map((row, i, array) => {
                const isLastRow = i === array.length - 1;
                return (
                  <React.Fragment key={i}>
                    <div className={`px-3 py-2 md:py-4 md:px-6 lg:py-4 lg:px-8 flex items-center text-[11px] md:text-sm font-medium uppercase tracking-wider leading-none md:leading-tight md:bg-white/15`}>
                      {row.logo ? (
                        <div className="flex items-center gap-2">
                          <img 
                            src={row.logo} 
                            alt=""
                            className="h-4 md:h-6 w-auto object-contain"
                            style={{ display: 'block' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <span className="text-gray-800">{row.feature.toUpperCase()}</span>
                </div>
                      ) : (
                        <span className="text-gray-800">{row.feature.toUpperCase()}</span>
                      )}
              </div>
                    <div className={`px-3 py-2 md:py-4 md:px-6 lg:py-4 lg:px-8 flex items-center justify-center md:bg-green-600/20`}>
                      <div className="w-5 h-5 md:w-8 md:h-8 rounded-full bg-green-600 flex items-center justify-center">
                        <Check className="w-3 h-3 md:w-5 md:h-5 text-white stroke-[3]" />
            </div>
          </div>
                    <div className={`px-3 py-2 md:py-4 md:px-6 lg:py-4 lg:px-8 flex items-center justify-center md:bg-white/15`}>
                      {row.others ? (
                        <div className="w-5 h-5 md:w-8 md:h-8 rounded-full bg-green-500 flex items-center justify-center">
                          <Check className="w-3 h-3 md:w-5 md:h-5 text-white stroke-[3]" />
              </div>
                      ) : (
                        <div className="w-5 h-5 md:w-8 md:h-8 rounded-full bg-gray-200/50 flex items-center justify-center">
                          <X className="w-3 h-3 md:w-5 md:h-5 text-gray-500/50 stroke-[3]" />
                        </div>
                      )}
            </div>
                  </React.Fragment>
                );
              })}
              </div>
            </motion.div>
        </div>
      </section>

        {/* Reviews - Clean Cards with enhanced scroll */}
        <section className="w-full bg-[#fafafa] pt-28 md:pt-32 pb-28 md:pb-32 px-6 overflow-hidden border-t border-gray-100">
          <div className="max-w-6xl mx-auto mb-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                className="text-[2.6rem] md:text-6xl font-semibold tracking-tight leading-[1.05]"
              >
                <span className="text-gray-900">Traveler Reviews</span>
              </motion.h2>
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-4">
                 <span className="text-gray-500 text-2xl md:text-lg font-medium">Excellent 4.5 out of 5</span>
               <div className="flex gap-2 md:gap-1.5">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="w-10 h-10 md:w-7 md:h-7 text-[#00b67a] fill-[#00b67a]" />
                  ))}
                  {/* Half star for 4.5 rating */}
                  <div className="relative w-10 h-10 md:w-7 md:h-7">
                    <Star className="absolute inset-0 w-full h-full text-gray-300 fill-gray-300" />
                    <div className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
                      <Star className="w-10 h-10 md:w-7 md:h-7 text-[#00b67a] fill-[#00b67a]" />
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>

          <style>{`
            @keyframes scroll-left {
              0% { transform: translate3d(0, 0, 0); }
              100% { transform: translate3d(-50%, 0, 0); }
            }
            .animate-scroll-left {
              animation: scroll-left 60s linear infinite;
              will-change: transform;
            }
          `}</style>

        <div className="relative -mx-6">
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />

            <div className="flex gap-6 animate-scroll-left pl-6" style={{ width: 'max-content' }}>
              {[...customerReviews, ...customerReviews].map((review, idx) => (
                <div key={`${review.id}-${idx}`} className="flex-shrink-0 w-[340px] md:w-[400px]">
                  <div className="bg-[#F5F5F7] rounded-3xl p-8 h-full hover:bg-white hover:shadow-xl transition-all duration-300 border border-transparent hover:border-gray-100">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-[#00b67a] fill-[#00b67a]" />
                    ))}
                  </div>
                    <p className="text-gray-700 text-base leading-relaxed mb-6 font-medium italic">
                    "{review.text}"
                  </p>
                    <div className="flex items-center gap-3 border-t border-gray-200/50 pt-4 mt-auto">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-white flex-shrink-0 shadow-sm">
                        <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                      </div>
                    <div>
                        <div className="text-gray-900 font-semibold">{review.name}</div>
                        <div className="text-gray-400 text-xs font-normal">Verified Traveler • {review.location}</div>
                    </div>
                  </div>
                </div>
              </div>
                    ))}
                  </div>
          </div>
        </section>

        {/* Award Banners - Clean Grid */}
        <section className="w-full bg-black py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-white mb-3 md:mb-4">Only on Defera</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  World's first.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-white/80 leading-relaxed max-w-xl mx-auto">
                You won't find these{' '}
                <span className="font-semibold text-white">anywhere else</span>.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {[
                {
                  icon: Award,
                  title: 'Exclusive rewards.',
                  desc: 'Defera is the world\u2019s first travel layaway service with a reward system. Triple dip by earning credit card points, frequent flyer miles, and Defera Miles.',
                },
                {
                  icon: Plane,
                  title: 'Semi-private flights.',
                  desc: 'Experience luxury for less. Book JSX semi-private flights with flexible payment plans — only available on Defera.',
                },
              ].map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: 0.05 + i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                  className="relative rounded-[28px] bg-[#161617] p-7 sm:p-10 overflow-hidden"
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-24 -right-24 w-72 h-72"
                    style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.22), transparent 65%)' }}
                  />
                  <span className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                    <Icon className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                  </span>
                  <h3 className="relative text-xl md:text-2xl font-semibold tracking-tight text-white leading-tight mb-2">
                    {title}
                  </h3>
                  <p className="relative text-[15px] md:text-base text-white/80 leading-relaxed max-w-md">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Triple Dip - Earn Miles 3 Ways */}
        <section ref={mapTriggerRef} className="w-full relative py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0" aria-hidden="true">
            <img src="/deferatwoways-1440.jpg" alt="" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/45 to-black/75" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-white mb-3 md:mb-4">Rewards, three ways</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] text-white mb-4 md:mb-6">
                The Triple Dip.
              </h2>
              <p className="text-[17px] md:text-lg text-white/80 leading-relaxed max-w-xl mx-auto">
                Every booking earns{' '}
                <span className="font-semibold text-white">three kinds of rewards at once</span> — no other travel
                layaway does that.
              </p>
            </motion.div>

            {/* Frosted glass tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  icon: CreditCard,
                  title: 'Credit card points',
                  desc: 'Pay with your rewards card at checkout. Earn points or cash back as you would on any purchase.',
                },
                {
                  icon: Plane,
                  title: 'Frequent flyer miles',
                  desc: 'Add your airline loyalty number when you book. Miles post directly to your account.',
                },
                {
                  icon: Award,
                  title: 'Defera Miles',
                  desc: '2 miles per dollar spent, automatically credited on every booking.',
                },
              ].map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: 0.05 + i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                  className="rounded-[28px] bg-black/55 backdrop-blur-xl p-7 sm:p-8"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.1] mb-5">
                    <Icon className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                  </span>
                  <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-white leading-tight mb-2">{title}</h3>
                  <p className="text-[15px] md:text-base text-white/80 leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Better than Klarna/Affirm. Not in the iOS app: App Review reads a
            comparison with lenders as lending (src/native/appStoreWording.js). */}
        {!isNativeApp && (
        <section className="w-full bg-[#f5f5f7] py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-[#1d1d1f] mb-3 md:mb-4">Why Defera</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #0F1952 0%, #243B7A 45%, #3D5A8C 70%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Better than
                  <br className="sm:hidden" />
                  {' '}Klarna &amp; Affirm.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-[#6e6e73] leading-relaxed max-w-xl mx-auto">
                Klarna and Affirm were built for retail checkout.{' '}
                <span className="font-semibold text-[#1d1d1f]">Defera was built for travel</span> — with price
                protection baked in.
              </p>
            </motion.div>

            {/* Comparison card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: 0.06, ease: [0.22, 0.61, 0.36, 1] }}
              className="max-w-4xl mx-auto rounded-[28px] bg-white overflow-hidden shadow-[0_2px_16px_rgba(0,0,0,0.05)]"
            >
              {/* Column headers — desktop */}
              <div className="hidden md:grid md:grid-cols-[10rem_1fr_1fr] border-b border-black/[0.06]">
                <div className="px-8 py-6" aria-hidden="true" />
                <div className="px-8 py-6">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-[#86868b] font-medium mb-2">Traditional</p>
                  <p className="text-base font-semibold text-[#6e6e73]">Buy Now, Pay Later</p>
                </div>
                <div className="px-8 py-6 bg-brand-50/60">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-brand-600 font-medium mb-2">Travel layaway</p>
                  <p className="text-base font-semibold text-[#1d1d1f]">Defera</p>
                </div>
              </div>

              {/* Comparison rows */}
              <div className="divide-y divide-black/[0.06]">
                {bnplComparison.map((row) => (
                  <div key={row.label}>
                    {/* Mobile — self-contained row cards */}
                    <div className="md:hidden px-4 sm:px-6 py-4">
                      <p className="text-[11px] uppercase tracking-[0.14em] text-[#86868b] font-medium mb-2.5">{row.label}</p>
                      <div className="grid grid-cols-2 rounded-2xl overflow-hidden border border-black/[0.06]">
                        <div className="px-3.5 py-3.5 border-r border-black/[0.06]">
                          <p className="text-[10px] uppercase tracking-[0.12em] text-[#86868b] font-medium mb-1.5">Traditional</p>
                          <p className="text-sm text-[#6e6e73] leading-snug">{row.bnpl}</p>
                        </div>
                        <div className="px-3.5 py-3.5 bg-brand-50/60">
                          <p className="text-[10px] uppercase tracking-[0.12em] text-brand-600 font-medium mb-1.5">Defera</p>
                          <p className="text-sm text-[#1d1d1f] font-medium leading-snug">{row.defera}</p>
                        </div>
                      </div>
                    </div>

                    {/* Desktop — table layout */}
                    <div className="hidden md:grid md:grid-cols-[10rem_1fr_1fr]">
                      <div className="px-8 py-5 flex items-center">
                        <span className="text-[11px] uppercase tracking-[0.14em] text-[#86868b] font-medium">{row.label}</span>
                      </div>
                      <div className="px-8 py-5 flex items-center">
                        <p className="text-[15px] text-[#6e6e73] leading-relaxed">{row.bnpl}</p>
                      </div>
                      <div className="px-8 py-5 flex items-center bg-brand-50/60">
                        <p className="text-[15px] text-[#1d1d1f] font-semibold leading-relaxed">{row.defera}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Footer line + CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8 md:mt-10 text-center"
            >
              <p className="text-[15px] md:text-base text-[#6e6e73] leading-relaxed mb-3">
                Book now, pay over time — no fine print, no credit checks, no caps.
              </p>
              <button
                type="button"
                onClick={() => navigate('/search')}
                className="inline-flex items-center gap-1.5 text-brand-600 font-medium text-[15px] md:text-base hover:underline underline-offset-4 transition-colors"
              >
                <span>Start booking</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </section>
        )}

        {/* Two Ways to Book */}
        <section className="w-full bg-[#f5f5f7] pb-20 md:pb-28 pt-4 md:pt-6 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-[#1d1d1f] mb-3 md:mb-4">Payment plans</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #0F1952 0%, #243B7A 45%, #3D5A8C 70%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Two ways to book.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-[#6e6e73] leading-relaxed max-w-xl mx-auto">
                Same price lock and zero interest on both — pick the deposit structure that fits your trip.
              </p>
            </motion.div>

            {/* Plan cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
              {/* Defera Installments — light tile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                className="rounded-[28px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.05)] p-7 sm:p-10 lg:p-11 flex flex-col"
              >
                <p className="text-sm font-medium text-brand-600 mb-4 md:mb-5">Defera Installments</p>
                <h3 className="text-[1.75rem] sm:text-[2.125rem] font-semibold tracking-tight text-[#1d1d1f] leading-[1.12] mb-3">
                  Small deposit today.
                  <br />
                  Pay the rest over time.
                </h3>
                <p className="text-[15px] md:text-base text-[#6e6e73] leading-relaxed mb-8 md:mb-10">
                  Works on any fare, any airline. Pay weekly or bi-weekly, and your e-ticket releases once your plan
                  is paid in full.
                </p>
                <ul className="space-y-4 mb-10 md:mb-12 flex-1">
                  {[
                    { icon: Clock, text: 'Weekly or bi-weekly schedule' },
                    { icon: Lock, text: 'Fare frozen from day one' },
                    { icon: Plane, text: 'All major airlines' },
                  ].map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-center gap-3.5 text-[15px] md:text-base text-[#1d1d1f]">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-50 flex-shrink-0">
                        <Icon className="w-4 h-4 text-brand-600" strokeWidth={1.8} />
                      </span>
                      {text}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => navigate('/search')}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-white bg-brand-600 hover:bg-brand-700 transition-colors w-full sm:w-fit self-start"
                >
                  <span>Start Booking</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>

              {/* Defera Zero — dark tile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 0.61, 0.36, 1] }}
                className="relative rounded-[28px] bg-[#101013] shadow-[0_2px_16px_rgba(0,0,0,0.1)] p-7 sm:p-10 lg:p-11 flex flex-col overflow-hidden"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-32 -right-32 w-96 h-96"
                  style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.35), transparent 65%)' }}
                />
                <p className="relative text-sm font-medium text-brand-200 mb-4 md:mb-5">Defera Zero</p>
                <h3 className="relative text-[1.75rem] sm:text-[2.125rem] font-semibold tracking-tight text-white leading-[1.12] mb-3">
                  $0 down at checkout.
                  <br />
                  Nothing due today.
                </h3>
                <p className="relative text-[15px] md:text-base text-white/80 leading-relaxed mb-6 md:mb-7">
                  On select refundable fares, your first payment lands on your scheduled date — and you can cancel up
                  to 24 hours before departure for a 75% refund.
                </p>
                <div className="relative mb-8 md:mb-10">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-normal mb-2">Available on</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    {[
                      { name: 'United', logo: 'https://www.united.com/favicon.ico' },
                      { name: 'American', logo: 'https://www.aa.com/favicon.ico' },
                      { name: 'Southwest', logo: 'https://www.southwest.com/favicon.ico' },
                      { name: 'JetBlue', logo: 'https://www.jetblue.com/favicon.ico' },
                      { name: 'Virgin Atlantic', logo: 'https://www.virginatlantic.com/favicon.ico' },
                    ].map(({ name, logo }) => (
                      <div key={name} className="flex items-center gap-1.5">
                        <img src={logo} alt={name} className="w-3.5 h-3.5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        <span className="text-[11px] text-white/50 font-normal">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <ul className="relative space-y-4 mb-10 md:mb-12 flex-1">
                  {[
                    { icon: Zap, text: 'Nothing due at checkout' },
                    { icon: Gift, text: '75% refund up to 24h before departure' },
                  ].map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-center gap-3.5 text-[15px] md:text-base text-white/85">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.08] flex-shrink-0">
                        <Icon className="w-4 h-4 text-brand-200" strokeWidth={1.8} />
                      </span>
                      {text}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => navigate('/search')}
                  className="relative inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-[#1d1d1f] bg-white hover:bg-white/90 transition-colors w-full sm:w-fit self-start"
                >
                  <span>Search Defera Zero Flights</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            </div>

            {/* Shared benefits — small bento tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mt-4 md:mt-5">
              {[
                { icon: CheckCircle, label: 'No credit check', sub: 'Everyone approved' },
                { icon: Percent, label: '0% interest', sub: 'On every booking' },
                { icon: Lock, label: 'Price lock', sub: 'From day one' },
              ].map(({ icon: Icon, label, sub }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: 0.05 + i * 0.06, ease: [0.22, 0.61, 0.36, 1] }}
                  className="rounded-[28px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.05)] px-6 py-7 md:py-8 text-center"
                >
                  <Icon className="w-7 h-7 text-brand-500 mx-auto mb-3" strokeWidth={1.6} />
                  <p className="text-base md:text-lg font-semibold text-[#1d1d1f]">{label}</p>
                  <p className="text-sm text-[#6e6e73] mt-1">{sub}</p>
                </motion.div>
              ))}
            </div>

            {/* Compare plans link */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8 md:mt-10 text-center"
            >
              <button
                type="button"
                onClick={() => navigate('/how-it-works')}
                className="inline-flex items-center gap-1.5 text-brand-600 font-medium text-[15px] md:text-base hover:underline underline-offset-4 transition-colors"
              >
                Compare plans in detail
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.p>
          </div>
        </section>

        {/* Live Route Map */}
        <section
          ref={mapSectionRef}
          className="hidden md:block w-full bg-black relative overflow-hidden"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(15,35,103,0.45), transparent 75%)' }} />

          {/* Header */}
          <div className="relative z-10 pt-16 md:pt-20 pb-0 px-6 text-center">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-sm md:text-base font-medium text-white mb-3 md:mb-4"
            >
              Live network
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06]"
            >
              <span
                style={{
                  background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Where our customers are flying.
              </span>
            </motion.h2>
          </div>

          {/* Map canvas — aspect ratio matches MapViz viewBox so there's zero letterbox padding */}
          <div
            className="relative z-10 w-full -mt-28 md:-mt-20 -mb-20 md:-mb-14"
            style={{ aspectRatio: isMobile ? '560 / 720' : '800 / 520' }}
          >
            {mapVisible ? (
              <Suspense fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full border-2 border-brand-400/30 border-t-brand-400/80 animate-spin" />
                </div>
              }>
                <MapViz routes={mapRoutes} />
              </Suspense>
            ) : (
              <div className="w-full h-full" />
            )}

            {/* Bottom fade so the map bleeds into the next section */}
            <div className="absolute bottom-0 inset-x-0 h-24 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #000)' }} />
          </div>

          {/* Footer note */}
          <div className="relative z-10 -mt-40 md:-mt-28 pb-12 md:pb-16 px-6 text-center">
            <p className="text-[11px] text-white/25 font-normal">
              Routes shown are based on real Defera bookings
            </p>
          </div>
        </section>

        {/* Defera Miles Rewards */}
        <section className="w-full bg-[#f5f5f7] py-20 md:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-[#1d1d1f] mb-3 md:mb-4">Rewards</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #0F1952 0%, #243B7A 45%, #3D5A8C 70%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Defera Miles.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-[#6e6e73] leading-relaxed max-w-xl mx-auto">
                The only travel layaway program with{' '}
                <span className="font-semibold text-[#1d1d1f]">a real rewards system</span>. Earn on every booking,
                redeem however you want.
              </p>
            </motion.div>

            {/* Hero card — stat + image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 0.61, 0.36, 1] }}
              className="rounded-[28px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.05)] overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-7 sm:p-10 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
                  <p className="text-[3rem] sm:text-[3.5rem] font-semibold tracking-tight text-[#1d1d1f] leading-[1.05]">
                    2 miles
                    <span className="block text-[1.25rem] sm:text-[1.375rem] font-normal text-[#6e6e73] mt-1">
                      per dollar spent
                    </span>
                  </p>
                  <p className="text-[15px] md:text-base text-[#6e6e73] leading-relaxed mt-5 mb-8 max-w-md">
                    Credited automatically when your booking completes. Miles never expire — stack them with credit
                    card points and frequent flyer miles.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/rewards')}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-white bg-brand-600 hover:bg-brand-700 transition-colors w-full sm:w-fit"
                  >
                    <span>Explore Rewards</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="relative min-h-[280px] sm:min-h-[340px] lg:min-h-[460px] order-1 lg:order-2">
                  <img
                    src="/deferamiles-1400.jpg"
                    alt="Defera Miles rewards"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover object-[30%_center]"
                  />
                </div>
              </div>
            </motion.div>

            {/* Redemption tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mt-4 md:mt-5">
              {[
                { icon: Plane, label: 'Travel perks', sub: 'Preferred seating, checked bags, cabin upgrades' },
                { icon: Gift, label: '200+ brand gift cards', sub: 'Starbucks, Amazon, Target, and more' },
                { icon: Award, label: 'Status tiers', sub: 'Silver, Gold, Platinum — unlock more as you travel' },
              ].map(({ icon: Icon, label, sub }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: 0.05 + i * 0.06, ease: [0.22, 0.61, 0.36, 1] }}
                  className="rounded-[28px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.05)] px-6 py-7 md:py-8 text-center"
                >
                  <Icon className="w-7 h-7 text-brand-500 mx-auto mb-3" strokeWidth={1.6} />
                  <p className="text-base md:text-lg font-semibold text-[#1d1d1f]">{label}</p>
                  <p className="text-sm text-[#6e6e73] mt-1 leading-relaxed">{sub}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Refundable Trip Benefits - Edge-to-edge banners (dark mode).
            Hidden for now: see SHOW_REFUNDABLE_PERKS_CARDS. */}
        {SHOW_REFUNDABLE_PERKS_CARDS && (
        <section className="w-full bg-zinc-950 overflow-visible">
          {/* American Airlines / Main Cabin Extra */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row min-h-[380px]"
          >
              <div className="lg:w-[45%] relative min-h-[340px] lg:min-h-[380px]">
                <img
                  src="/maincabinextra.jpg"
                  alt="Main Cabin Extra seating"
                  className="w-full h-full min-h-[340px] lg:min-h-[380px] object-cover object-[center_70%]"
                />
              </div>
              <div
                className="flex-1 lg:w-[55%] flex flex-col justify-center items-start p-10 md:p-14 lg:p-16"
                style={{
                  background: 'linear-gradient(135deg, #0E122A 0%, #040614 40%, #0E1643 100%)',
                }}
              >
                <img src="/aa3.png" alt="" className="h-12 md:h-14 w-auto mb-6 object-contain" />
                <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
                  {isMobile ? (
                    <>Make It Refundable,<br />Get the Perks</>
                  ) : (
                    'Make It Refundable, Get the Perks'
                  )}
                </h2>
                <p className="text-gray-300 text-base leading-relaxed mb-8 max-w-xl">
                  When you make your American Airlines trip refundable, receive extra legroom seating, priority boarding, complimentary beer, wine & spirits—plus 75% refund if plans change.
                </p>
                <div className="flex flex-wrap gap-3 mb-8">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-gray-200 text-sm font-medium">
                    <Armchair className="w-4 h-4 text-brand-400" />
                    Extra legroom
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-gray-200 text-sm font-medium">
                    <Plane className="w-4 h-4 text-brand-400" />
                    Priority boarding
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-gray-200 text-sm font-medium">
                    <Wine className="w-4 h-4 text-brand-400" />
                    Complimentary drinks
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-gray-200 text-sm font-medium">
                    <Gift className="w-4 h-4 text-brand-400" />
                    $10 Starbucks Gift Card
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-lg bg-[#080E28] hover:bg-[#060A1E] text-white font-semibold text-sm transition-colors"
                >
                  Search Flights
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[9px] text-gray-500/50 leading-tight mt-6 max-w-xl">
                  Not all flights offer the refundable option. Supplies are limited and availability may vary. American Airlines® and related marks are trademarks of American Airlines, Inc. Defera is not affiliated with, endorsed by, or sponsored by American Airlines, Inc. Any reference to or use of American Airlines trademarks is for informational purposes only and does not constitute an official partnership or endorsement.
                </p>
              </div>
          </motion.div>

          {/* All Other Flights / Starbucks */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col lg:flex-row min-h-[380px] overflow-visible"
          >
              <div className="lg:w-[45%] relative min-h-[340px] lg:min-h-[380px]">
                <img
                  src="/starbucks.jpg"
                  alt="Starbucks"
                  className="w-full h-full min-h-[340px] lg:min-h-[380px] object-cover object-center"
                />
              </div>
              <div
                className="flex-1 lg:w-[55%] flex flex-col justify-center items-start p-10 md:p-14 lg:p-16 overflow-visible"
                style={{
                  background: 'linear-gradient(135deg, #0f1f14 0%, #1a3326 40%, #0f2619 100%)',
                }}
              >
                <img src="/starbucks2.png" alt="" className="h-12 md:h-14 w-auto mb-6 object-contain" />
                <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
                  Let Us Treat You
                </h2>
                <p className="text-gray-300 text-base leading-relaxed mb-8 max-w-xl">
                  Book refundable on any U.S. based airline and receive a $10 Starbucks Gift Card with every booking.
                </p>
                <div
                  className={`flex items-center gap-4 mb-8 relative overflow-visible w-fit ${isMobile ? 'rounded-full py-2.5 px-7 gap-3' : ''}`}
                  style={isMobile ? {
                    background: 'rgba(42, 83, 64, 0.7)',
                    boxShadow: '0 0 40px rgba(110, 231, 183, 0.7), 0 0 80px rgba(110, 231, 183, 0.5), inset 0 0 100px rgba(110, 231, 183, 0.9)',
                  } : undefined}
                >
                  <div className="absolute inset-0 -mx-6 -my-1 blur-xl bg-emerald-300/50 rounded-full hidden md:block" aria-hidden />
                  <img src="/stars.png" alt="" className={`object-contain flex-shrink-0 relative z-10 ${isMobile ? 'w-10 h-10' : 'w-16 h-16'}`} />
                  <span className={`font-semibold text-white relative z-10 whitespace-nowrap ${isMobile ? 'text-sm' : 'text-lg'}`}>$10 Starbucks Gift Card</span>
                </div>
                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-lg bg-[#00704a] hover:bg-[#005a38] text-white font-semibold text-sm transition-colors"
                >
                  Search Flights
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[9px] text-gray-500/50 leading-tight mt-6 max-w-xl">
                  Dispersed upon completion of the installment plan. Restrictions apply. Offer subject to availability and may be modified or discontinued at any time without notice. Terms and conditions apply. Starbucks® and related marks are trademarks of Starbucks Corporation. Defera is not affiliated with, endorsed by, or sponsored by Starbucks Corporation. Any reference to or use of Starbucks trademarks is for informational purposes only and does not constitute an official partnership or endorsement.
                </p>
              </div>
          </motion.div>
        </section>
        )}

        {/* FAQ Section - Clean Accordion */}
        <section className="w-full bg-[#fafafa] py-28 md:py-32 px-6 relative">
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <motion.h2 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                className="text-[2.6rem] md:text-6xl font-semibold mb-6 tracking-tight leading-[1.05]"
              >
              <span className="text-gray-900">FAQs</span>
              </motion.h2>
              <p className="text-lg md:text-2xl font-light leading-snug md:leading-normal">
                <span className="text-gray-600">Everything you need to know</span>
              </p>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto">
            {HOME_FAQS.map((faq, index) => (
              <motion.div
                key={index}
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
                  transition={{ duration: 0.6, delay: index * 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                  whileHover={{ scale: 1.01 }}
                  className="bg-white rounded-2xl px-8 py-6 shadow-sm hover:shadow-md cursor-default"
                >
                  <h3 className="font-semibold mb-2 text-lg">
                    <span className="text-gray-900">{faq.q}</span>
                  </h3>
                  <p className="leading-relaxed">
                    <span className="text-gray-600">{faq.a}</span>
                  </p>
              </motion.div>
            ))}
          </div>

            <div className="text-center mt-12">
            <button
              onClick={() => navigate('/help')}
                className="text-brand-600 hover:text-brand-700 font-semibold transition-colors"
            >
              View all FAQs →
            </button>
          </div>
        </div>
      </section>

        {/* Concierge - Clean */}
        <section ref={conciergeSectionRef} className="w-full bg-[#f5f5f7] py-20 md:py-28 overflow-hidden">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-center mb-10 md:mb-14"
            >
              <p className="text-sm md:text-base font-medium text-[#1d1d1f] mb-3 md:mb-4">Support</p>
              <h2 className="text-[2.5rem] sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.06] mb-4 md:mb-6">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #0F1952 0%, #243B7A 45%, #3D5A8C 70%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Award-winning
                  <br className="sm:hidden" />
                  {' '}customer service.
                </span>
              </h2>
              <p className="text-[17px] md:text-lg text-[#6e6e73] leading-relaxed max-w-xl mx-auto">
                Defera holds itself to{' '}
                <span className="font-semibold text-[#1d1d1f]">a higher standard</span> than any other layaway
                service on the planet.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div
                initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
            className="flex justify-center"
            >
              {/* Phone Mockup with Text Messages */}
              <div className="relative w-[340px]">
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-[3rem] p-3" style={{ 
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)'
                }}>
                  {/* Dynamic Island */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-full z-10" />
                  
                  {/* Screen */}
                  <div className="bg-white rounded-[2.5rem] overflow-hidden shadow-inner">
                    {/* Status Bar */}
                    <div className="flex items-center justify-between px-5 pt-4 pb-2.5">
                      <span className="text-black text-xs font-medium">9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-2 border border-black rounded-sm">
                          <div className="w-full h-full bg-black rounded-sm" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Message Header */}
                    <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="text-black text-sm font-medium">Defera Concierge</div>
                        <div className="text-gray-500 text-xs">Online now</div>
                      </div>
                    </div>
                    
                    {/* Messages */}
                    <div className="px-5 py-5 space-y-4 bg-gray-50 min-h-[400px]">
                      {/* User Message 1 */}
                      <motion.div 
                        className="flex justify-end"
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ 
                          opacity: messageCycle >= 1 ? 1 : 0, 
                          y: messageCycle >= 1 ? 0 : 10,
                          scale: messageCycle >= 1 ? 1 : 0.95
                        }}
                        transition={{ duration: 0.4 }}
                      >
                        <div className="bg-black text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%]">
                          <p className="text-sm leading-tight">My flight was just cancelled and I have a meeting in two days 😰</p>
                          <span className="text-xs text-white/60 mt-1 block">11:42 AM</span>
                        </div>
                      </motion.div>
                      
                      {/* Concierge Response 1 */}
                      <motion.div 
                        className="flex justify-start"
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ 
                          opacity: messageCycle >= 2 ? 1 : 0, 
                          y: messageCycle >= 2 ? 0 : 10,
                          scale: messageCycle >= 2 ? 1 : 0.95
                        }}
                        transition={{ duration: 0.4 }}
                      >
                        <div className="bg-white text-gray-900 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[80%] shadow-sm">
                          <p className="text-sm leading-tight">We noticed that change on our end and have already rebooked you on the next available flight. Same cabin class, no additional charges. ✈️</p>
                          <span className="text-xs text-gray-500 mt-1 block">11:44 AM</span>
                        </div>
                      </motion.div>
                      
                      {/* User Message 2 */}
                      <motion.div 
                        className="flex justify-end"
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ 
                          opacity: messageCycle >= 3 ? 1 : 0, 
                          y: messageCycle >= 3 ? 0 : 10,
                          scale: messageCycle >= 3 ? 1 : 0.95
                        }}
                        transition={{ duration: 0.4 }}
                      >
                        <div className="bg-black text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%]">
                          <p className="text-sm leading-tight">That was incredibly fast. Is there anything I need to confirm?</p>
                          <span className="text-xs text-white/60 mt-1 block">11:44 AM</span>
                        </div>
                      </motion.div>
                      
                      {/* Concierge Response 2 */}
                      <motion.div 
                        className="flex justify-start"
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ 
                          opacity: messageCycle >= 4 ? 1 : 0, 
                          y: messageCycle >= 4 ? 0 : 10,
                          scale: messageCycle >= 4 ? 1 : 0.95
                        }}
                        transition={{ duration: 0.4 }}
                      >
                        <div className="bg-white text-gray-900 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[80%] shadow-sm">
                          <p className="text-sm leading-tight">Everything is taken care of. Your updated boarding pass has been sent to your email—no action required on your part. 👍</p>
                          <span className="text-xs text-gray-500 mt-1 block">11:45 AM</span>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
            >
              {/* Concierge Team Image - Circular Badge */}
              <div className="mb-6 md:mb-8 flex justify-center md:justify-start">
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-[0_2px_16px_rgba(0,0,0,0.1)]">
                  <img
                    src="/images/defera-team-1000.jpg"
                    alt="Defera Concierge Team"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

                {/* The phone line leads, chat follows (founder, 2026-10-24: "We
                    need to promote this more than chat support"). */}
                <h3 className="text-[1.75rem] sm:text-[2.125rem] font-semibold tracking-tight text-[#1d1d1f] leading-[1.12] mb-3 text-center md:text-left">
                Have any questions?
                <br />
                Ask our team now.
              </h3>
                <p className="text-[15px] md:text-base text-[#6e6e73] leading-relaxed mb-6 text-center md:text-left max-w-md mx-auto md:mx-0">
                Booking, payments, or your trip: call us any time, day or night.
              </p>

                <div className="flex flex-col items-center md:items-start gap-2 mb-8 md:mb-10">
                  <div className="flex items-center gap-2 bg-white text-[#1d1d1f] px-4 py-2 rounded-full text-sm font-medium shadow-[0_2px_16px_rgba(0,0,0,0.05)]">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Answering calls now, 24/7
                  </div>
                </div>

              <div className="flex flex-col items-center md:items-start gap-3">
                <a
                  href={SUPPORT_PHONE_TEL}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-white bg-brand-600 hover:bg-brand-700 transition-colors w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4" aria-hidden />
                  Call {SUPPORT_PHONE_DISPLAY}
                </a>
                <Link
                  to="/support"
                  className="inline-flex items-center gap-1 text-[14px] font-medium text-[#6e6e73] hover:text-[#1d1d1f] transition-colors"
                >
                  Prefer to chat? Chat with Concierge
                  <ArrowRight className="w-3.5 h-3.5" aria-hidden />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

        {/* "Your first Defera trip" — emotional close, full-bleed on every
             viewport. Placed right before the CTA as the final moment of
             aspiration before the ask. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
          className="w-full bg-black"
        >
          <div className="overflow-hidden bg-black md:grid md:grid-cols-2 md:items-stretch">
            <div className="relative">
              <img
                src="/bathtub.jpg"
                alt="Unwinding at a hotel on your first Defera trip"
                width="2333"
                height="3500"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
              />
            </div>
            <div className="px-6 py-14 md:px-14 lg:px-20 md:py-24 md:flex md:flex-col md:justify-center">
              <div className="md:max-w-md">
                <div className="text-[11px] uppercase tracking-[0.22em] font-normal text-white/40 mb-3">
                  The moment is yours
                </div>
                <h3 className="text-2xl md:text-[2rem] font-semibold tracking-tight leading-[1.1] text-white mb-3">
                  Your first Defera trip is closer than it has ever been.
                </h3>
                <p className="text-white/80 text-[15px] md:text-base font-normal leading-relaxed">
                  That vacation will not book itself. But with installments alongside you, it no longer needs to wait for the next paycheck.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section - Clean & Direct - Moved to bottom before footer */}
        <section className="w-full py-28 md:py-32 px-6 relative overflow-hidden">
          {/* Beautiful background image */}
          <img
            src="https://images.pexels.com/photos/4451508/pexels-photo-4451508.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          
          {/* Opacity overlay for text readability */}
          <div className="absolute inset-0 bg-black/20" />
          
          {/* Subtle decorative elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-white/10 rounded-full blur-3xl -mt-[500px] pointer-events-none" />
          
          <div className="max-w-6xl mx-auto text-center relative z-10">
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-[2.6rem] md:text-6xl font-semibold mb-6 tracking-tight leading-[1.05]"
            >
              <span className="text-white">Ready to book?</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-lg md:text-2xl mb-12 max-w-xl mx-auto font-light leading-snug md:leading-normal"
            >
              <span className="text-white">No credit check. No interest. Book flights with 500+ airlines and pay over time.</span>
            </motion.p>
            {/* Defera Promotional Video - Mobile Only, inside Ready to book */}
            <div className="md:hidden mb-8 mx-auto max-w-md">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                className="rounded-2xl overflow-hidden bg-black relative"
                style={{ paddingBottom: '100%' }}
              >
                <iframe 
                  src={videoSrc}
                  loading="lazy"
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Defera Promotional Video"
                />
              </motion.div>
            </div>
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-black hover:bg-gray-800 text-white px-6 py-3.5 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all w-full sm:w-auto inline-flex items-center justify-center gap-2"
              >
                Start Your Search
                <ArrowRight className="w-4 h-4" />
              </Button>
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/70 hover:text-white font-medium text-sm transition-colors inline-flex items-center gap-1.5 py-2"
              >
                Learn how it works
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Footer - Apple Dark Gray Style */}
      <footer className="w-full bg-black py-28 md:py-32 px-6 text-xs text-zinc-600">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8 mb-16">
            <div className="col-span-2">
              <img
                src="/defera-logo-web.png"
                alt="Defera"
                loading="lazy"
                className="h-8 md:h-10 opacity-80 mb-6"
              />
              <p className="text-zinc-600 leading-relaxed max-w-sm">
                Defera is the leading Buy Now, Pay Later platform for travel. We empower travelers to explore the world with flexible payment plans, no hidden fees, and no credit checks.
              </p>
            </div>

            <div>
              <h4 className="text-zinc-600 font-medium mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-zinc-600 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/careers" className="text-zinc-600 hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/press" className="text-zinc-600 hover:text-white transition-colors">Press</Link></li>
                <li><Link to="/contact" className="text-zinc-600 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-zinc-600 font-medium mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/how-it-works" className="text-zinc-600 hover:text-white transition-colors">How it Works</Link></li>
                <li><Link to="/rewards" className="text-zinc-600 hover:text-white transition-colors">Rewards</Link></li>
                <li><Link to="/help" className="text-zinc-600 hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/security" className="text-zinc-600 hover:text-white transition-colors">Security</Link></li>
                <li><Link to="/guides" className="text-zinc-600 hover:text-white transition-colors">Guides</Link></li>
                <li><Link to="/terms" className="text-zinc-600 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-zinc-600 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/privacy-choices" className="text-zinc-600 hover:text-white transition-colors">Do Not Sell or Share My Personal Information</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 space-y-6">
            <div className="space-y-2.5">
              <p className="font-normal text-zinc-600">
                Copyright © {new Date().getFullYear()} Defera LLC. All Rights Reserved
              </p>
              <p className="text-[10px] font-normal leading-tight text-zinc-600 max-w-4xl">
                Defera LLC is partnered with a fully accredited organization in multiple countries that maintains direct and indirect relationships with various airline carriers and hotel chains. Some carriers and hotels may not be available for booking due to the absence of an established partnership. All cancellation and change policies are governed by the respective airline and not by Defera LLC. Defera LLC is a financial technology company providing payment processing and installment services. Defera LLC is not a bank, credit union, or other depository institution. All financial services are provided by our partner financial institutions. Terms and conditions apply to all services. Please review our Terms of Service and Privacy Policy for complete information regarding our services, data practices, and user obligations.
              </p>
            </div>
            
            <div className="flex items-center gap-6 pt-2">
              <button onClick={() => navigate('/home?auth_mode=login')} className="text-zinc-600 hover:text-white transition-colors">
                Sign In
              </button>
              <span className="text-zinc-600">|</span>
              <button onClick={() => navigate('/home?auth_mode=signup')} className="text-zinc-600 hover:text-white transition-colors">
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
